import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, TextField, Grid, CircularProgress,
  Alert, Chip, Divider, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Tooltip, LinearProgress, Table, TableHead, TableBody,
  TableRow, TableCell,
  Autocomplete, Checkbox, FormControlLabel, Switch, Menu, MenuItem
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  DragIndicator as DragIcon,
  ContentCopy as ContentCopyIcon,
  PhotoCamera as PhotoCameraIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  MoreHoriz as MoreIcon
} from '@mui/icons-material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { leesJson, meldFout } from '../../utils/apiFout';
import DuplicateCallsheetDialog from './DuplicateCallsheetDialog';
import ReistijdRegel from './ReistijdRegel';
import { useCallsheetFotos, FotoStrook, PlanningFotoCel, WordExportKnop } from './CallsheetFotos';
import CallsheetBijlagen, { aantalBijlagen, BijlagenInDialoog, bijlagenProbleem, informeerCrew } from './CallsheetBijlagen';
import CallsheetVerzendCheck, { feitenUitCallsheet, zelfdeOntvangers } from './CallsheetVerzendCheck';
import ProjectStrook, { ProjectOpenenKnop, kaleProjectnaam } from './ProjectStrook';
import {
  KlikVeld, KlaarRegel, Sectie, Rij, bepaalOntbrekend, splitsOplevering, voegOpleveringSamen,
  TekstOpmaak, losseRegelsAlsLijst, tekstOpmaakSx, WAARDE_PX, REGELHOOGTE,
  formatTelefoon, stripBedrijf, bestandsnaamUitHeader,
  sorteerPlanning, tijdVoorInvoegen, tijdNaVerslepen, STANDAARD_DUUR_MIN,
  contactStatus, CONTACT_VELDEN, GEEN_CONTACT, settelefoonVan, tekstKnopSx, ORANJE, GRIJS, LIJN,
  SECTIE_ACCENT, SECTIE_TITEL, RolChip, tijdSx, INKT,
} from './callsheetDocument';
import { useMsal } from '@azure/msal-react';
import { createDraftMessage } from '../../utils/graphMailSearch';
import { stripBronRefs, bronRefsNaarLinks, mdLinkComponents } from '../../utils/briefingBronnen';
import useAiVoorbereiden from './useAiVoorbereiden';
import AiVoortgang from './AiVoortgang';
import { crewMetDubbelen } from '../../utils/crewDubbelen';
import KlantVereistenBlok from '../../ui-components/KlantVereistenBlok';
import { huidigeBlokken, leegBijStart, kiesInvulling, blokVanVeld, vulBlokken, maakControle, biedDeelOngedaanAan, voorstelUitJob, herstelBlok, naHerstelBijwerken } from './aiVoorbereiden';

// Dubbele crew (wizard + Airtable-automation, 21 okt) niet tonen. Elke getoonde regel draagt de ids van zijn
// verborgen kopieën (`_dubbelen`) mee, zodat verwijderen/vervangen die ook opruimt en ze niet terugkomen.
const metUniekeCrew = (d) => (d && Array.isArray(d.crew) ? { ...d, crew: crewMetDubbelen(d.crew) } : d);
const verwijderCrewDubbelen = async (ids) => {
  const uitkomst = await Promise.all((ids || []).map((dubbelId) =>
    authFetch(`${BACKEND_URL}/Api/callsheet-crew/${dubbelId}`, { method: 'DELETE' }).then((r) => r.ok, () => false)));
  const mislukt = uitkomst.filter((ok) => !ok).length;
  if (mislukt) throw new Error(`${mislukt} verborgen dubbele crewregel(s) niet verwijderd — probeer het opnieuw`);
};

// Function options for crew
const FUNCTIE_OPTIONS = [
  'Regisseur', 'Opnameleider', 'Camera Operator', 'Cinematograaf / DOP',
  'Camera Assistent', '1e Camera Assistent', '2e Camera Assistent',
  'Fotograaf', 'Fotograaf Assistent', 'Gaffer', 'Belichtingsassistent / Best Boy',
  'Geluidstechnicus', 'Geluid Assistent', 'Producer', 'Productie Assistent',
  'Art Director', 'Setdresser', 'Visagist', 'Stylist', 'Food Stylist',
  'Editor', 'Opdrachtgever', 'Assistent'
];

// Format duration (seconds) to HH:MM
const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return '';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// Parse HH:MM to seconds
const parseDuration = (str) => {
  if (!str) return null;
  const [hours, minutes] = str.split(':').map(Number);
  return (hours || 0) * 3600 + (minutes || 0) * 60;
};

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('nl-NL', { 
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' 
    });
  } catch { return dateStr; }
};

// ============ CREW DIALOG ============
function CrewDialog({ open, onClose, onSave, initialData, callsheetId, personen = [] }) {
  const [selectedPersoon, setSelectedPersoon] = useState(null);
  const [persoon, setPersoon] = useState('');
  const [functie, setFunctie] = useState('');
  const [calltijd, setCalltijd] = useState('');
  const [telefoon, setTelefoon] = useState('');
  const [opmerkingen, setOpmerkingen] = useState('');
  const [saving, setSaving] = useState(false);
  const [useManual, setUseManual] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [creatingContact, setCreatingContact] = useState(false);

  // Maak een nieuw, herbruikbaar Personen-record aan en koppel het meteen.
  const createCrewContact = async () => {
    const nm = (window.prompt('Naam van het nieuwe crew-contact:', persoon || '') || '').trim();
    if (!nm) return;
    setCreatingContact(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/callsheet-new-crew-contact`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ naam: nm, telefoon }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setSelectedPersoon({ id: data.id, fields: { Naam: data.naam, 'Volledige naam': data.naam } });
      setPersoon(data.naam);
      toast.success(`Contact "${data.naam}" aangemaakt`);
    } catch (err) {
      toast.error(`Aanmaken mislukt: ${err.message}`);
    } finally {
      setCreatingContact(false);
    }
  };

  useEffect(() => {
    if (initialData) {
      setPersoon(initialData.fields?.Persoon || '');
      setFunctie(initialData.fields?.['Functie (handmatig)'] || '');
      setCalltijd(formatDuration(initialData.fields?.Calltijd));
      setTelefoon(initialData.fields?.['Telefoon (handmatig)'] || '');
      setOpmerkingen(initialData.fields?.Opmerkingen || '');
      // Try to find the linked person
      const linkedPersonId = initialData.fields?.Personen?.[0];
      if (linkedPersonId && personen.length > 0) {
        const found = personen.find(p => p.id === linkedPersonId);
        setSelectedPersoon(found || null);
        setUseManual(!found);
      } else {
        setSelectedPersoon(null);
        setUseManual(true);
      }
      // Show advanced fields if editing or if any advanced field has data
      setShowAdvanced(true);
    } else {
      setSelectedPersoon(null);
      setPersoon('');
      setFunctie('');
      setCalltijd('');
      setTelefoon('');
      setOpmerkingen('');
      setUseManual(false);
      setShowAdvanced(false); // Hide advanced fields for new entries
    }
  }, [initialData, open, personen]);

  const handlePersonSelect = (event, newValue) => {
    setSelectedPersoon(newValue);
    if (newValue) {
      setPersoon(newValue.fields?.['Volledige naam'] || newValue.fields?.Naam || '');
      // Auto-fill telefoonnummer uit de gekozen persoon.
      const tel = newValue.fields?.Telefoonnummer || '';
      if (tel) setTelefoon(Array.isArray(tel) ? tel[0] : String(tel));
      // Auto-fill functie if available
      const functies = newValue.fields?.Functie;
      if (functies && functies.length > 0) {
        const functieArr = Array.isArray(functies) ? functies : [functies];
        // Try to match with our FUNCTIE_OPTIONS
        const matched = FUNCTIE_OPTIONS.find(f =>
          functieArr.some(cf => String(cf).toLowerCase().includes(f.toLowerCase()) || f.toLowerCase().includes(String(cf).toLowerCase()))
        );
        // Vul altijd in: matchende standaardfunctie, anders de ruwe functie van de persoon.
        setFunctie(matched || String(functieArr[0] || ''));
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        persoon: selectedPersoon ? (selectedPersoon.fields?.['Volledige naam'] || selectedPersoon.fields?.Naam || persoon) : persoon,
        // Leveranciers-base personen (_source) komen uit een andere Airtable-base en kunnen
        // NIET als linked record worden opgeslagen → bewaar als snapshot (naam+functie+tel).
        personenId: (selectedPersoon && !selectedPersoon._source) ? selectedPersoon.id : null,
        functie,
        calltijd: parseDuration(calltijd),
        telefoon,
        opmerkingen,
        callsheetId
      };

      if (initialData?.id) {
        // Update existing
        const response = await authFetch(`${BACKEND_URL}/Api/callsheet-crew/${initialData.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            'Persoon': data.persoon,
            'Personen': data.personenId ? [data.personenId] : undefined,
            'Functie (handmatig)': functie,
            'Calltijd': parseDuration(calltijd),
            'Telefoon (handmatig)': telefoon,
            'Opmerkingen': opmerkingen
          })
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || `HTTP ${response.status}`);
        }
        // Verborgen kopieën van dit crewlid (zelfde persoon, geen eigen gegevens) gaan mee weg.
        await verwijderCrewDubbelen(initialData._dubbelen);
      } else {
        // Create new
        console.log('[CrewDialog] Creating crew with:', data);
        const response = await authFetch(`${BACKEND_URL}/Api/callsheet-crew`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          console.error('[CrewDialog] Error response:', errData);
          throw new Error(errData.error || `HTTP ${response.status}`);
        }
        const result = await response.json();
        console.log('[CrewDialog] Created:', result);
      }
      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving crew:', err);
      toast.error(`Fout bij opslaan: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const displayName = selectedPersoon 
    ? (selectedPersoon.fields?.['Volledige naam'] || selectedPersoon.fields?.Naam || persoon) 
    : persoon;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initialData ? 'Crew bewerken' : 'Crew toevoegen'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Teamledenlijst kon niet laden → val terug op handmatig invoeren (nooit lege dialog). */}
          {personen.length === 0 && !initialData && (
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              Teamledenlijst is niet geladen — vul de crew hieronder handmatig in (of herlaad de pagina).
            </Typography>
          )}
          {/* Toggle buttons voor selecteren vs handmatig */}
          {personen.length > 0 && !initialData && (
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button 
                variant={!useManual ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setUseManual(false)}
                sx={{ flex: 1 }}
              >
                📋 Selecteren
              </Button>
              <Button 
                variant={useManual ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setUseManual(true)}
                sx={{ flex: 1 }}
              >
                ✏️ Handmatig
              </Button>
            </Box>
          )}

          {/* OPTIE 1: Selecteren uit database */}
          {!useManual && personen.length > 0 && (
            <Autocomplete
              options={personen}
              value={selectedPersoon}
              onChange={handlePersonSelect}
              getOptionLabel={(option) => option.fields?.['Volledige naam'] || option.fields?.Naam || ''}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {option.fields?.['Volledige naam'] || option.fields?.Naam}
                    </Typography>
                    {option.fields?.Functie && (
                      <Typography variant="caption" color="text.secondary">
                        {Array.isArray(option.fields.Functie) ? option.fields.Functie.join(', ') : option.fields.Functie}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Selecteer teamlid" placeholder="Zoek op naam..." />
              )}
              noOptionsText="Geen teamleden gevonden"
              fullWidth
            />
          )}
          {!useManual && !initialData && (
            <Button size="small" startIcon={<AddIcon />} onClick={createCrewContact} disabled={creatingContact}
              sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
              {creatingContact ? 'Aanmaken…' : 'Nieuw contact aanmaken'}
            </Button>
          )}

          {/* OPTIE 2: Handmatig invoeren - extra velden (ook als de lijst leeg/niet geladen is) */}
          {(useManual || initialData || personen.length === 0) && (
            <>
              <Autocomplete
                freeSolo
                options={FUNCTIE_OPTIONS}
                value={functie}
                onChange={(_e, v) => setFunctie(v || '')}
                onInputChange={(_e, v, reason) => { if (reason === 'input') setFunctie(v); }}
                renderInput={(params) => <TextField {...params} label="Functie (kies of typ 'anders')" fullWidth />}
              />
              <TextField
                label="Calltijd (HH:MM)"
                value={calltijd}
                onChange={(e) => setCalltijd(e.target.value)}
                placeholder="09:00"
                fullWidth
              />
              <TextField
                label="Telefoon"
                value={telefoon}
                onChange={(e) => setTelefoon(e.target.value)}
                fullWidth
              />
              <TextField
                label="Opmerkingen"
                value={opmerkingen}
                onChange={(e) => setOpmerkingen(e.target.value)}
                multiline
                rows={2}
                fullWidth
                inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
              />
              
              {/* Naam veld bij handmatige invoer (of als de lijst niet geladen is) */}
              {(useManual || (personen.length === 0 && !initialData)) && (
                <>
                  <Divider />
                  <TextField
                    label="Naam (handmatig)"
                    value={persoon}
                    onChange={(e) => setPersoon(e.target.value)}
                    fullWidth
                    autoFocus={personen.length === 0}
                    helperText="Naam van crew member als deze niet in de database staat"
                  />
                </>
              )}
            </>
          )}

          {/* Extra velden knop alleen bij selecteren */}
          {!useManual && !initialData && selectedPersoon && !showAdvanced && (
            <Button 
              size="small" 
              onClick={() => setShowAdvanced(true)}
              sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
              color="secondary"
            >
              + Extra velden (functie, calltijd, etc.)
            </Button>
          )}

          {/* Extra velden bij selecteren */}
          {!useManual && !initialData && showAdvanced && (
            <>
              <Divider sx={{ my: 1 }} />
              <Autocomplete
                freeSolo
                options={FUNCTIE_OPTIONS}
                value={functie}
                onChange={(_e, v) => setFunctie(v || '')}
                onInputChange={(_e, v, reason) => { if (reason === 'input') setFunctie(v); }}
                renderInput={(params) => <TextField {...params} label="Functie (kies of typ 'anders')" fullWidth />}
              />
              <TextField
                label="Calltijd (HH:MM)"
                value={calltijd}
                onChange={(e) => setCalltijd(e.target.value)}
                placeholder="09:00"
                fullWidth
              />
              <TextField
                label="Telefoon"
                value={telefoon}
                onChange={(e) => setTelefoon(e.target.value)}
                fullWidth
              />
              <TextField
                label="Opmerkingen"
                value={opmerkingen}
                onChange={(e) => setOpmerkingen(e.target.value)}
                multiline
                rows={2}
                fullWidth
                inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuleren</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || (!selectedPersoon && !persoon)}>
          {saving ? <CircularProgress size={20} /> : 'Opslaan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Inline vrije-keuze veld voor de planning-tabel: kies uit de bovenaan toegevoegde
// items (locaties/cast) OF typ een eigen waarde. Commit op blur/enter (niet per toets).
function PlanningCombo({ value, options = [], onCommit, placeholder }) {
  const [val, setVal] = useState(value || '');
  useEffect(() => { setVal(value || ''); }, [value]);
  const commit = (v) => { const s = (v ?? '').trim(); if (s !== (value || '')) onCommit(s); };
  return (
    <Autocomplete
      freeSolo size="small" options={options}
      value={val} inputValue={val}
      onInputChange={(_e, v) => setVal(v)}
      onChange={(_e, v) => { setVal(v || ''); commit(v || ''); }}
      onBlur={() => commit(val)}
      renderInput={(params) => (
        <TextField {...params} variant="standard" placeholder={placeholder}
          sx={{ fontSize: 13, minWidth: 100, '& .MuiInput-root': { fontSize: 13 } }} />
      )}
      sx={{ minWidth: 110 }}
    />
  );
}

// Multi-select cast voor de planning-tabel: kies meerdere cast-leden (of typ vrij).
// Wordt opgeslagen als komma-gescheiden tekst in het multilineText-veld 'Cast'.
function PlanningCastMulti({ value, options = [], onCommit, placeholder }) {
  const toArr = (v) => String(v || '').split(/\s*,\s*/).map(s => s.trim()).filter(Boolean);
  const [vals, setVals] = useState(toArr(value));
  useEffect(() => { setVals(toArr(value)); }, [value]);
  const commit = (arr) => {
    const joined = arr.join(', ');
    if (joined !== (value || '')) onCommit(joined);
  };
  return (
    <Autocomplete
      freeSolo multiple size="small" options={options} value={vals}
      onChange={(_e, v) => {
        const arr = v.map(s => String(s).trim()).filter(Boolean);
        setVals(arr); commit(arr);
      }}
      ChipProps={{ size: 'small' }}
      renderInput={(params) => (
        <TextField {...params} variant="standard" placeholder={vals.length ? '' : placeholder}
          sx={{ fontSize: 13, minWidth: 120, '& .MuiInput-root': { fontSize: 13 } }} />
      )}
      sx={{ minWidth: 130 }}
    />
  );
}

// ============ PLANNING DIALOG ============
// `standaard` (nieuwe regel): { tijd (sec), duur (min) } — begintijd waar hij tussen komt (Joris 29 sep).
function PlanningDialog({ open, onClose, onSave, onAfterSave, initialData, callsheetId, standaard }) {
  const [tijd, setTijd] = useState('');
  const [duur, setDuur] = useState('');
  const [omschrijving, setOmschrijving] = useState('');
  const [locatie, setLocatie] = useState('');
  const [cast, setCast] = useState('');
  const [opmerkingen, setOpmerkingen] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initialData) {
      setTijd(formatDuration(initialData.fields?.Tijd));
      setDuur(initialData.fields?.['Duur (minuten)'] || '');
      setOmschrijving(initialData.fields?.Omschrijving || '');
      setLocatie(initialData.fields?.Locatie || '');
      setCast(initialData.fields?.Cast || '');
      setOpmerkingen(initialData.fields?.Opmerkingen || '');
    } else {
      setTijd(standaard ? formatDuration(standaard.tijd) : '');
      setDuur(standaard ? String(standaard.duur) : '');
      setOmschrijving('');
      setLocatie('');
      setCast('');
      setOpmerkingen('');
    }
  }, [initialData, open, standaard]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (initialData?.id) {
        await authFetch(`${BACKEND_URL}/Api/callsheet-planning/${initialData.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            'Tijd': parseDuration(tijd),
            'Duur (minuten)': parseInt(duur) || 0,
            'Omschrijving': omschrijving,
            'Locatie': locatie,
            'Cast': cast,
            'Opmerkingen': opmerkingen
          })
        });
      } else {
        await authFetch(`${BACKEND_URL}/Api/callsheet-planning`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            callsheetId,
            tijd: parseDuration(tijd),
            duur: parseInt(duur) || 0,
            omschrijving,
            locatie,
            cast,
            opmerkingen
          })
        });
      }
      if (onAfterSave) {
        await onAfterSave();
      } else if (onSave) {
        onSave();
      }
      onClose();
    } catch (err) {
      console.error('Error saving planning:', err);
      toast.error('Fout bij opslaan planning');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initialData ? 'Planning bewerken' : 'Planning toevoegen'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Tijd (HH:MM)"
                value={tijd}
                onChange={(e) => setTijd(e.target.value)}
                placeholder="09:00"
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Duur (minuten)"
                value={duur}
                onChange={(e) => setDuur(e.target.value)}
                type="number"
                fullWidth
              />
            </Grid>
          </Grid>
          <TextField
            label="Omschrijving"
            value={omschrijving}
            onChange={(e) => setOmschrijving(e.target.value)}
            fullWidth
          />
          <TextField
            label="Locatie"
            value={locatie}
            onChange={(e) => setLocatie(e.target.value)}
            fullWidth
          />
          <TextField
            label="Cast"
            value={cast}
            onChange={(e) => setCast(e.target.value)}
            fullWidth
          />
          <TextField
            label="Opmerkingen"
            value={opmerkingen}
            onChange={(e) => setOpmerkingen(e.target.value)}
            multiline
            rows={2}
            fullWidth
            inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuleren</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Opslaan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============ CAST DIALOG ============
function CastDialog({ open, onClose, onSave, initialData, callsheetId, castingOptions = [] }) {
  const [selectedCast, setSelectedCast] = useState(null);
  const [naam, setNaam] = useState('');
  const [rol, setRol] = useState('');
  const [calltijd, setCalltijd] = useState('');
  const [telefoon, setTelefoon] = useState('');
  const [opmerkingen, setOpmerkingen] = useState('');
  const [saving, setSaving] = useState(false);
  const [useManual, setUseManual] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [creatingContact, setCreatingContact] = useState(false);

  // Maak een nieuw, herbruikbaar Casting-record aan en koppel het meteen.
  const createCastContact = async () => {
    const nm = (window.prompt('Naam van het nieuwe cast-contact:', naam || '') || '').trim();
    if (!nm) return;
    setCreatingContact(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/callsheet-new-cast-contact`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ naam: nm }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setSelectedCast({ id: data.id, fields: { Naam: data.naam } });
      setNaam(data.naam);
      toast.success(`Contact "${data.naam}" aangemaakt`);
    } catch (err) {
      toast.error(`Aanmaken mislukt: ${err.message}`);
    } finally {
      setCreatingContact(false);
    }
  };

  useEffect(() => {
    if (initialData) {
      setNaam(initialData.fields?.['Cast (handmatig)'] || '');
      setRol(initialData.fields?.Rol || '');
      setCalltijd(formatDuration(initialData.fields?.Calltijd));
      setTelefoon(initialData.fields?.['Telefoon (handmatig)'] || '');
      setOpmerkingen(initialData.fields?.Opmerkingen || '');
      // Try to find the linked cast
      const linkedCastId = initialData.fields?.Cast?.[0];
      if (linkedCastId && castingOptions.length > 0) {
        const found = castingOptions.find(c => c.id === linkedCastId);
        setSelectedCast(found || null);
        setUseManual(!found);
      } else {
        setSelectedCast(null);
        setUseManual(true);
      }
      setShowAdvanced(true); // Show advanced fields when editing
    } else {
      setSelectedCast(null);
      setNaam('');
      setRol('');
      setCalltijd('');
      setTelefoon('');
      setOpmerkingen('');
      setUseManual(false);
      setShowAdvanced(false); // Hide advanced fields for new entries
    }
  }, [initialData, open, castingOptions]);

  const handleCastSelect = (event, newValue) => {
    setSelectedCast(newValue);
    if (newValue) {
      setNaam(newValue.fields?.Naam || '');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (initialData?.id) {
        const response = await authFetch(`${BACKEND_URL}/Api/callsheet-cast/${initialData.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            'Cast': selectedCast ? [selectedCast.id] : undefined,
            'Cast (handmatig)': naam,
            'Rol': rol,
            'Calltijd': parseDuration(calltijd),
            'Telefoon (handmatig)': telefoon,
            'Opmerkingen': opmerkingen
          })
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || `HTTP ${response.status}`);
        }
      } else {
        const payload = {
          callsheetId,
          castId: selectedCast?.id,
          naam: naam || (selectedCast?.fields?.Naam || ''),
          rol,
          calltijd: parseDuration(calltijd),
          telefoon,
          opmerkingen
        };
        console.log('[CastDialog] Creating cast with:', payload);
        
        const response = await authFetch(`${BACKEND_URL}/Api/callsheet-cast`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          console.error('[CastDialog] Error response:', errData);
          throw new Error(errData.error || `HTTP ${response.status}`);
        }
        
        const result = await response.json();
        console.log('[CastDialog] Created:', result);
      }
      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving cast:', err);
      toast.error(`Fout bij opslaan: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initialData ? 'Cast bewerken' : 'Cast toevoegen'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Toggle buttons voor selecteren vs handmatig */}
          {castingOptions.length > 0 && !initialData && (
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button 
                variant={!useManual ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setUseManual(false)}
                sx={{ flex: 1 }}
              >
                📋 Selecteren
              </Button>
              <Button 
                variant={useManual ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setUseManual(true)}
                sx={{ flex: 1 }}
              >
                ✏️ Handmatig
              </Button>
            </Box>
          )}

          {/* OPTIE 1: Selecteren uit database */}
          {!useManual && castingOptions.length > 0 && (
            <Autocomplete
              options={castingOptions}
              value={selectedCast}
              onChange={handleCastSelect}
              getOptionLabel={(option) => option.fields?.Naam || ''}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Typography variant="body2" fontWeight={500}>
                    {option.fields?.Naam}
                  </Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Selecteer cast" placeholder="Zoek op naam..." />
              )}
              noOptionsText="Geen cast gevonden"
              fullWidth
            />
          )}
          {!useManual && !initialData && (
            <Button size="small" startIcon={<AddIcon />} onClick={createCastContact} disabled={creatingContact}
              sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
              {creatingContact ? 'Aanmaken…' : 'Nieuw contact aanmaken'}
            </Button>
          )}

          {/* OPTIE 2: Handmatig invoeren - extra velden */}
          {(useManual || initialData) && (
            <>
              {/* Naam veld bovenaan bij handmatige invoer */}
              {useManual && (
                <TextField
                  label="Naam"
                  value={naam}
                  onChange={(e) => setNaam(e.target.value)}
                  fullWidth
                  autoFocus
                  placeholder="Naam van cast member"
                  helperText="Naam als deze niet in de database staat"
                />
              )}
              <TextField
                label="Rol"
                value={rol}
                onChange={(e) => setRol(e.target.value)}
                fullWidth
                placeholder="bijv. Hoofdpersoon, Figurant..."
              />
              <TextField
                label="Calltijd (HH:MM)"
                value={calltijd}
                onChange={(e) => setCalltijd(e.target.value)}
                placeholder="09:00"
                fullWidth
              />
              <TextField
                label="Telefoon"
                value={telefoon}
                onChange={(e) => setTelefoon(e.target.value)}
                fullWidth
              />
              <TextField
                label="Opmerkingen"
                value={opmerkingen}
                onChange={(e) => setOpmerkingen(e.target.value)}
                multiline
                rows={2}
                fullWidth
                inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
              />
            </>
          )}

          {/* Extra velden knop alleen bij selecteren */}
          {!useManual && !initialData && selectedCast && !showAdvanced && (
            <Button 
              size="small" 
              onClick={() => setShowAdvanced(true)}
              sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
              color="secondary"
            >
              + Extra velden (rol, calltijd, etc.)
            </Button>
          )}

          {/* Extra velden bij selecteren */}
          {!useManual && !initialData && showAdvanced && (
            <>
              <Divider sx={{ my: 1 }} />
              <TextField
                label="Rol"
                value={rol}
                onChange={(e) => setRol(e.target.value)}
                fullWidth
                placeholder="bijv. Hoofdpersoon, Figurant..."
              />
              <TextField
                label="Calltijd (HH:MM)"
                value={calltijd}
                onChange={(e) => setCalltijd(e.target.value)}
                placeholder="09:00"
                fullWidth
              />
              <TextField
                label="Telefoon"
                value={telefoon}
                onChange={(e) => setTelefoon(e.target.value)}
                fullWidth
              />
              <TextField
                label="Opmerkingen"
                value={opmerkingen}
                onChange={(e) => setOpmerkingen(e.target.value)}
                multiline
                rows={2}
                fullWidth
                inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuleren</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || (!selectedCast && !naam)}>
          {saving ? <CircularProgress size={20} /> : 'Opslaan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============ LOCATIE DIALOG ============
// Adres-autocomplete via PDOK Locatieserver (gratis, geen API-key, NL-adressen, CORS)
// + bedrijfsnaam/POI-zoek via OSM Nominatim (zo vind je "Alliander" / "Enexis" op naam).
// onSelect krijgt {straat, postcode, plaats, naam?}. Vult de locatievelden.
const PDOK_BASE = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1';
const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org';
function PdokAdresZoeker({ onSelect, eerderGebruikt = [] }) {
  const [opts, setOpts] = useState([]);
  const [loading, setLoading] = useState(false);
  const seqRef = useRef(0);
  const debounceRef = useRef(null);
  const fetchSuggest = (q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 3) { setOpts([]); return; }
    debounceRef.current = setTimeout(async () => {
      const seq = ++seqRef.current;
      setLoading(true);
      try {
        // PDOK (adressen) + Nominatim (bedrijfsnamen/POI's) parallel; merge tot één lijst.
        // bewust stil: externe suggest-bronnen, als één bron uitvalt tonen we de andere gewoon.
        const [pdok, osm] = await Promise.all([
          fetch(`${PDOK_BASE}/suggest?q=${encodeURIComponent(q)}&fq=type:adres&rows=6`)
            .then(r => r.json()).catch(() => null),
          fetch(`${NOMINATIM_BASE}/search?q=${encodeURIComponent(q)}&format=jsonv2&countrycodes=nl&addressdetails=1&limit=5`)
            .then(r => r.json()).catch(() => null),
        ]);
        if (seq !== seqRef.current) return;
        const pdokOpts = (pdok?.response?.docs || []).map(d => ({
          id: `pdok:${d.id}`, source: 'pdok', pdokId: d.id, label: d.weergavenaam,
        }));
        const osmOpts = (Array.isArray(osm) ? osm : []).map((d, i) => {
          const a = d.address || {};
          return {
            id: `osm:${d.place_id || i}`, source: 'osm',
            label: `🏢 ${d.display_name}`,
            naam: a.amenity || a.office || a.building || a.shop || (d.name) || (d.display_name || '').split(',')[0],
            straat: [a.road, a.house_number].filter(Boolean).join(' ').trim(),
            postcode: (a.postcode || '').toUpperCase(),
            plaats: a.city || a.town || a.village || a.municipality || '',
          };
        });
        // Eerder gebruikte locaties (eigen bibliotheek) altijd bovenaan: "Huis Tonneke" moet
        // de Tilburgse van vorige keer geven, niet een gelijknamige POI elders (Joris, 7 sep).
        const zoek = q.toLowerCase();
        const eigen = (eerderGebruikt || []).filter((l) => {
          const f = l.fields || {};
          return [f['Naam locatie'], f.Straat, f.Plaats, f.Adres].some((v) => String(v || '').toLowerCase().includes(zoek));
        }).slice(0, 5).map((l) => {
          const f = l.fields || {};
          return {
            id: `eigen:${l.id}`, source: 'eigen',
            label: `⭐ ${f['Naam locatie'] || 'Locatie'} — ${[f.Straat, f.Plaats].filter(Boolean).join(', ')} (eerder gebruikt)`,
            naam: f['Naam locatie'] || '', straat: f.Straat || '', postcode: f.Postcode || '', plaats: f.Plaats || '',
            parkeren: f.Parkeren || '', contactpersoon: f.Contactpersoon || '', telefoon: f.Telefoonnummer || '',
          };
        });
        setOpts([...eigen, ...osmOpts, ...pdokOpts]);
      } catch { /* stil */ } finally { if (seq === seqRef.current) setLoading(false); }
    }, 300);
  };
  const lookupPdok = async (pdokId) => {
    try {
      const res = await fetch(`${PDOK_BASE}/lookup?id=${encodeURIComponent(pdokId)}&fl=straatnaam,huis_nlt,postcode,woonplaatsnaam`);
      const doc = (await leesJson(res, 'Adres opzoeken (PDOK)'))?.response?.docs?.[0];
      if (!doc) return;
      const pc = (doc.postcode || '').replace(/(\d{4})\s?([A-Za-z]{2})/, '$1 $2').toUpperCase();
      onSelect({
        straat: [doc.straatnaam, doc.huis_nlt].filter(Boolean).join(' ').trim(),
        postcode: pc,
        plaats: doc.woonplaatsnaam || '',
      });
    } catch (e) { meldFout(e, 'Adres opzoeken (PDOK)'); }
  };
  const handleSelect = (val) => {
    if (!val) return;
    if (val.source === 'pdok') { lookupPdok(val.pdokId); return; }
    // OSM/eigen: adresdetails zitten al in de optie.
    const pc = (val.postcode || '').replace(/(\d{4})\s?([A-Za-z]{2})/, '$1 $2').toUpperCase();
    onSelect({ straat: val.straat || '', postcode: pc, plaats: val.plaats || '', naam: val.naam || '',
      parkeren: val.parkeren || '', contactpersoon: val.contactpersoon || '', telefoon: val.telefoon || '', eigen: val.source === 'eigen' });
  };
  return (
    <Autocomplete
      options={opts}
      loading={loading}
      filterOptions={(x) => x}   // server-side filtering (PDOK + Nominatim)
      getOptionLabel={(o) => o?.label || ''}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      onInputChange={(_e, val, reason) => { if (reason === 'input') fetchSuggest(val); }}
      onChange={(_e, val) => handleSelect(val)}
      noOptionsText="Typ minstens 3 tekens…"
      renderInput={(params) => <TextField {...params} size="small" label="Adres of bedrijf zoeken"
        placeholder="Straat huisnummer, of bedrijfsnaam (bv. Alliander)…" />}
      sx={{ mb: 1 }}
    />
  );
}

// Contactpersoon klant — het zoekveld verschijnt ín het veld (klik → veld, Joris 28 sep).
// Zoekt in Bedrijven + intern; een treffer vult naam én telefoon. Staat de persoon er niet in,
// dan typ je de naam en bewaart Enter (of klik-buiten) hem handmatig. Esc annuleert.
function KlantContactZoekVeld({ begin = '', origineel, onKies, onHandmatig, onAnnuleer, onGeenContact }) {
  const [opts, setOpts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invoer, setInvoer] = useState(begin);
  const seqRef = useRef(0);          // alleen het resultaat van de LAATSTE query tonen
  const debounceRef = useRef(null);
  const klaarRef = useRef(false);    // na kiezen/annuleren geen tweede actie via de blur
  useEffect(() => () => clearTimeout(debounceRef.current), []);
  const fetchOpts = (q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const seq = ++seqRef.current;
      setLoading(true);
      try {
        const res = await authFetch(`${BACKEND_URL}/Api/callsheet-klant-contacten?q=${encodeURIComponent(q || '')}`);
        const data = await leesJson(res, 'Klantcontacten laden');
        if (seq === seqRef.current) setOpts(data.contacten || []);  // negeer verouderde antwoorden
      } catch (e) { if (seq === seqRef.current) { meldFout(e, 'Klantcontacten laden'); setOpts([]); } } finally {
        if (seq === seqRef.current) setLoading(false);
      }
    }, 250);
  };
  const eenmaal = (fn) => { if (klaarRef.current) return; klaarRef.current = true; fn(); };
  // Enter of klik-buiten bewaart wat er staat — ook leeg (contact weghalen); alleen Esc annuleert.
  const bewaarInvoer = (tekst) => {
    // Vergelijk met wat er echt staat: vanaf 'geen contact' ('-') is leegmaken wél een wijziging.
    const t = String(tekst || '').trim();
    if (t === (origineel !== undefined ? origineel : begin)) onAnnuleer(); else onHandmatig(t);
  };
  return (
    <Box>
    <Autocomplete
      freeSolo
      options={opts}
      loading={loading}
      inputValue={invoer}
      filterOptions={(x) => x}   // server-side zoeken (op naam ÉN bedrijf) — geen client-filter
      onInputChange={(_e, val, reason) => {
        if (reason === 'input') { setInvoer(val); fetchOpts(val); }
        else if (reason === 'clear') setInvoer('');
      }}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o?.naam || '')}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      onChange={(_e, val) => {
        if (val && typeof val === 'object') eenmaal(() => onKies(val));
        else if (typeof val === 'string') eenmaal(() => bewaarInvoer(val));
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') { e.preventDefault(); eenmaal(onAnnuleer); }
        // Een leeg veld + Enter geeft bij freeSolo geen onChange; leegmaken moet wél bewaren (Codex r1).
        else if (e.key === 'Enter' && !invoer.trim()) { e.preventDefault(); eenmaal(() => bewaarInvoer('')); }
      }}
      onBlur={() => eenmaal(() => bewaarInvoer(invoer))}
      renderOption={(props, o) => (
        <Box component="li" {...props} key={o.id}>
          <Box>
            <Typography variant="body2" fontWeight={500}>{o.naam}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[o.bedrijf, formatTelefoon(o.telefoon)].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => <TextField {...params} size="small" variant="standard" autoFocus
        placeholder="Zoek op naam of bedrijf (Bedrijven + intern)…"
        inputProps={{ ...params.inputProps, 'aria-label': 'Contactpersoon klant zoeken' }}
        helperText="Kies een treffer (vult ook de telefoon) of typ een naam + Enter · leegmaken = terug naar het contact uit het project · Esc annuleert" />}
      noOptionsText={invoer.trim() ? 'Niet gevonden — Enter bewaart de naam handmatig' : 'Typ om te zoeken'}
      sx={{ maxWidth: 480 }}
    />
    {onGeenContact && (
      // onMouseDown voorkomt de blur van het zoekveld (die zou anders eerst bewaren).
      <Button size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => eenmaal(onGeenContact)}
        sx={{ textTransform: 'none', color: '#64748b', ml: -1, mt: 0.25 }}>
        Geen contact op deze callsheet
      </Button>
    )}
    </Box>
  );
}

// Settelefoon: de Full Frame-persoon die de settelefoon draagt, gekozen ín het veld. Schrijft het
// gekoppelde veld 'Contactpersoon Settelefoon' (link naar Personen); het nummer zoekt Airtable op
// (FullFrame Settelefoonnummer). Alleen koppelbare hoofd-base personen (geen Leveranciers-base _source).
function SettelefoonZoekVeld({ personen, onKies, onAnnuleer }) {
  const naamVan = (p) => p?.fields?.['Volledige naam'] || p?.fields?.Naam || '';
  const telVan = (p) => {
    const t = p?.fields?.Telefoonnummer;
    return Array.isArray(t) ? t[0] : (t || '');
  };
  const klaarRef = useRef(false);
  const eenmaal = (fn) => { if (klaarRef.current) return; klaarRef.current = true; fn(); };
  const options = (personen || []).filter(p => !p._source);
  return (
    <Autocomplete
      options={options}
      openOnFocus
      getOptionLabel={naamVan}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      onChange={(_e, val) => { if (val) eenmaal(() => onKies(val)); }}
      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); eenmaal(onAnnuleer); } }}
      onBlur={() => eenmaal(onAnnuleer)}
      renderOption={(props, o) => (
        <Box component="li" {...props} key={o.id}>
          <Box>
            <Typography variant="body2" fontWeight={500}>{naamVan(o)}</Typography>
            {telVan(o) && <Typography variant="caption" color="text.secondary">{formatTelefoon(telVan(o))}</Typography>}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField {...params} size="small" variant="standard" autoFocus placeholder="Wie draagt de settelefoon?"
          inputProps={{ ...params.inputProps, 'aria-label': 'Settelefoon kiezen' }} helperText="Esc annuleert" />
      )}
      noOptionsText="Geen personen"
      sx={{ maxWidth: 480 }}
    />
  );
}

// Parse een geplakt adresblok naar losse velden. Werkt op regels (of komma's bij één regel).
// NL-postcode `1234 AB`, URL, straat (regel met huisnummer), naam (eerste regel).
function parsePastedLocation(text) {
  let lines = String(text || '').split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length <= 1 && text.includes(',')) lines = text.split(',').map(l => l.trim()).filter(Boolean);
  const out = { naam: '', straat: '', postcode: '', plaats: '', url: '' };
  const used = new Set();
  const pcRe = /\b(\d{4})\s?([A-Za-z]{2})\b/;
  const urlRe = /(https?:\/\/\S+|www\.\S+)/i;
  // URL
  lines.forEach((l, i) => { const m = l.match(urlRe); if (m && !out.url) { out.url = m[0]; used.add(i); } });
  // Postcode + plaats
  lines.forEach((l, i) => {
    if (used.has(i) || out.postcode) return;
    const m = l.match(pcRe);
    if (m) {
      out.postcode = `${m[1]} ${m[2].toUpperCase()}`;
      const rest = l.replace(m[0], '').replace(/^[,\s-]+|[,\s-]+$/g, '').trim();
      if (rest) out.plaats = rest;
      used.add(i);
    }
  });
  // Naam = eerste ongebruikte regel
  for (let i = 0; i < lines.length; i++) { if (!used.has(i)) { out.naam = lines[i]; used.add(i); break; } }
  // Straat = eerste resterende regel met een cijfer (huisnummer)
  for (let i = 0; i < lines.length; i++) { if (!used.has(i) && /\d/.test(lines[i])) { out.straat = lines[i]; used.add(i); break; } }
  return out;
}

function LocatieDialog({ open, onClose, onSave, initialData, callsheetId, existingLocaties = [] }) {
  const [pasteText, setPasteText] = useState('');
  const [selectedLocatie, setSelectedLocatie] = useState(null);
  const [naam, setNaam] = useState('');
  const [straat, setStraat] = useState('');
  const [postcode, setPostcode] = useState('');
  const [plaats, setPlaats] = useState('');
  const [contactpersoon, setContactpersoon] = useState('');
  const [telefoon, setTelefoon] = useState('');
  const [opmerkingen, setOpmerkingen] = useState('');
  const [parkeren, setParkeren] = useState('');
  const [parkeerfoto, setParkeerfoto] = useState('');   // multiline: 1 (signed) blob-URL per regel
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [parkSuggest, setParkSuggest] = useState(null);  // centrale parkeer-lookup voorstel
  const [saving, setSaving] = useState(false);
  const [useManual, setUseManual] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const fotoUrls = parkeerfoto.split('\n').map(s => s.trim()).filter(Boolean);
  const handleFotoUpload = async (file) => {
    if (!file) return;
    setUploadingFoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await authFetch(`${BACKEND_URL}/Api/callsheet-parking-image`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setParkeerfoto(prev => [...prev.split('\n').map(s => s.trim()).filter(Boolean), data.url].join('\n'));
    } catch (err) {
      toast.error(`Upload mislukt: ${err.message}`);
    } finally {
      setUploadingFoto(false);
    }
  };
  const removeFoto = (url) => setParkeerfoto(prev => prev.split('\n').map(s => s.trim()).filter(Boolean).filter(u => u !== url).join('\n'));

  // Centrale "parkeer-database per adres": zoek of dit adres al eerder parkeerinfo had.
  const lookupParking = async (s, pc, pl) => {
    if (!pc && !s) return;
    try {
      const qs = new URLSearchParams({ straat: s || '', postcode: pc || '', plaats: pl || '' });
      const res = await authFetch(`${BACKEND_URL}/Api/callsheet-parking-lookup?${qs}`);
      const data = await leesJson(res, 'Parkeerinfo opzoeken');
      if (data?.match && (data.match.parkeren || data.match.parkeerfoto)) setParkSuggest(data.match);
    } catch (e) { console.warn('[LocatieDialog] parkeer-lookup:', e.message); /* bewust stil: optionele suggestie, geen blokkade voor het formulier */ }
  };

  useEffect(() => {
    if (initialData) {
      setNaam(initialData.fields?.['Naam locatie'] || '');
      setStraat(initialData.fields?.Straat || '');
      setPostcode(initialData.fields?.Postcode || '');
      setPlaats(initialData.fields?.Plaats || '');
      setContactpersoon(initialData.fields?.Contactpersoon || '');
      setTelefoon(initialData.fields?.Telefoonnummer || '');
      setOpmerkingen(initialData.fields?.Opmerkingen || '');
      setParkeren(initialData.fields?.Parkeren || '');
      setParkeerfoto(initialData.fields?.Parkeerfoto || '');
      setUseManual(true); // When editing, show manual fields
      setShowAdvanced(true); // Show all fields when editing
    } else {
      setSelectedLocatie(null);
      setNaam('');
      setStraat('');
      setPostcode('');
      setPlaats('');
      setContactpersoon('');
      setTelefoon('');
      setOpmerkingen('');
      setParkeren('');
      setParkeerfoto('');
      setUseManual(existingLocaties.length === 0); // If no existing locations, show manual
      setShowAdvanced(false); // Hide advanced fields for new entries
    }
    setParkSuggest(null);
  }, [initialData, open, existingLocaties.length]);

  const handleLocatieSelect = (event, newValue) => {
    setSelectedLocatie(newValue);
    if (newValue) {
      // Pre-fill fields from selected location
      setNaam(newValue.fields?.['Naam locatie'] || '');
      setStraat(newValue.fields?.Straat || '');
      setPostcode(newValue.fields?.Postcode || '');
      setPlaats(newValue.fields?.Plaats || '');
      setContactpersoon(newValue.fields?.Contactpersoon || '');
      setTelefoon(newValue.fields?.Telefoonnummer || '');
      setParkeren(newValue.fields?.Parkeren || '');
      setParkeerfoto(newValue.fields?.Parkeerfoto || '');
      // Don't copy opmerkingen - let user add their own
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (initialData?.id) {
        const response = await authFetch(`${BACKEND_URL}/Api/callsheet-locaties/${initialData.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            'Naam locatie': naam,
            'Straat': straat,
            'Postcode': postcode,
            'Plaats': plaats,
            'Contactpersoon': contactpersoon,
            'Telefoonnummer': telefoon,
            'Opmerkingen': opmerkingen,
            'Parkeren': parkeren,
            'Parkeerfoto': parkeerfoto
          })
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || `HTTP ${response.status}`);
        }
      } else {
        const payload = {
          callsheetId,
          naam,
          straat,
          postcode,
          plaats,
          contactpersoon,
          telefoon,
          opmerkingen,
          parkeren,
          parkeerfoto
        };
        console.log('[LocatieDialog] Creating location with:', payload);
        const response = await authFetch(`${BACKEND_URL}/Api/callsheet-locaties`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          console.error('[LocatieDialog] Error response:', errData);
          throw new Error(errData.error || `HTTP ${response.status}`);
        }
        const result = await response.json();
        console.log('[LocatieDialog] Created:', result);
      }
      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving locatie:', err);
      toast.error(`Fout bij opslaan: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Gedeeld blok: parkeerfoto's uploaden + tonen (gebruikt in beide invoer-paden).
  const parkeerfotoBlock = (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Button component="label" size="small" variant="outlined" startIcon={<PhotoCameraIcon fontSize="small" />} disabled={uploadingFoto}>
          {uploadingFoto ? 'Uploaden…' : 'Parkeerfoto toevoegen'}
          <input hidden type="file" accept="image/*" capture="environment"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; handleFotoUpload(f); }} />
        </Button>
        {uploadingFoto && <CircularProgress size={16} />}
      </Box>
      {fotoUrls.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          {fotoUrls.map((url) => (
            <Box key={url} sx={{ position: 'relative', width: 84, height: 64 }}>
              <Box component="img" src={url} alt="parkeerfoto"
                sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1, border: '1px solid #e5e7eb' }} />
              <IconButton size="small" onClick={() => removeFoto(url)}
                sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff', boxShadow: 1, p: 0.25, '&:hover': { bgcolor: '#fee2e2' } }}>
                <DeleteIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initialData ? 'Locatie bewerken' : 'Locatie toevoegen'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Toggle buttons voor selecteren vs handmatig */}
          {existingLocaties.length > 0 && !initialData && (
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button 
                variant={!useManual ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setUseManual(false)}
                sx={{ flex: 1 }}
              >
                📋 Selecteren
              </Button>
              <Button 
                variant={useManual ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setUseManual(true)}
                sx={{ flex: 1 }}
              >
                ✏️ Handmatig
              </Button>
            </Box>
          )}

          {/* OPTIE 1: Selecteren uit database */}
          {!useManual && existingLocaties.length > 0 && !initialData && (
            <Autocomplete
              options={existingLocaties}
              value={selectedLocatie}
              onChange={handleLocatieSelect}
              getOptionLabel={(option) => {
                const naam = option.fields?.['Naam locatie'] || '';
                const plaats = option.fields?.Plaats || '';
                return plaats ? `${naam} (${plaats})` : naam;
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {option.fields?.['Naam locatie']}
                    </Typography>
                    {(option.fields?.Straat || option.fields?.Plaats) && (
                      <Typography variant="caption" color="text.secondary">
                        {[option.fields?.Straat, option.fields?.Postcode, option.fields?.Plaats].filter(Boolean).join(', ')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Selecteer bestaande locatie" placeholder="Zoek op naam of plaats..." />
              )}
              noOptionsText="Geen locaties gevonden"
              fullWidth
            />
          )}

          {/* OPTIE 2: Handmatig invoeren of bewerken */}
          {(useManual || initialData) && (
            <>
              {/* Adres zoeken via PDOK (gratis) → vult straat/postcode/plaats. */}
              <PdokAdresZoeker
                eerderGebruikt={existingLocaties}
                onSelect={({ straat: s, postcode: pc, plaats: pl, naam: nm, parkeren: pk, eigen }) => {
                  if (s) setStraat(s);
                  if (pc) setPostcode(pc);
                  if (pl) setPlaats(pl);
                  if (nm && (!naam || eigen)) setNaam(nm);   // bedrijfsnaam invullen als nog leeg (eigen locatie: altijd)
                  if (pk && eigen && !parkeren) setParkeren(pk);
                  if (!parkeren && !pk) lookupParking(s, pc, pl);  // bekend parkeeradvies voorstellen
                }}
              />
              {parkSuggest && !parkeren && (
                <Alert severity="info" sx={{ py: 0.5 }}
                  action={
                    <Button color="inherit" size="small" onClick={() => {
                      setParkeren(parkSuggest.parkeren || '');
                      if (parkSuggest.parkeerfoto) setParkeerfoto(parkSuggest.parkeerfoto);
                      setParkSuggest(null);
                    }}>
                      Overnemen
                    </Button>
                  }>
                  Parkeerinfo bekend voor dit adres{parkSuggest.naam ? ` (${parkSuggest.naam})` : ''}:
                  <Box component="span" sx={{ display: 'block', fontStyle: 'italic', mt: 0.5 }}>
                    {(parkSuggest.parkeren || '').slice(0, 120)}{(parkSuggest.parkeren || '').length > 120 ? '…' : ''}
                  </Box>
                </Alert>
              )}
              {/* Snel invullen: plak een adresblok (naam / straat / postcode plaats / url). */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  label="Plak adres (naam, straat, postcode plaats, url)"
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                  size="small"
                  placeholder={'Hercuton\nTouwslager 2\n5253 RK Nieuwkuijk\nhttps://www.hercuton.nl/'}
                />
                <Button
                  variant="outlined"
                  disabled={!pasteText.trim()}
                  onClick={() => {
                    const p = parsePastedLocation(pasteText);
                    if (p.naam) setNaam(p.naam);
                    if (p.straat) setStraat(p.straat);
                    if (p.postcode) setPostcode(p.postcode);
                    if (p.plaats) setPlaats(p.plaats);
                    if (p.url) setOpmerkingen((prev) => prev ? `${prev}\n${p.url}` : p.url);
                    setPasteText('');
                  }}
                  sx={{ whiteSpace: 'nowrap', mt: 0.5 }}
                >
                  Invullen
                </Button>
              </Box>
              <TextField
                label="Straat + huisnummer"
                value={straat}
                onChange={(e) => setStraat(e.target.value)}
                fullWidth
              />
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    label="Postcode"
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value)}
                    onBlur={() => { if (!parkeren) lookupParking(straat, postcode, plaats); }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    label="Plaats"
                    value={plaats}
                    onChange={(e) => setPlaats(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
              <TextField
                label="Contactpersoon"
                value={contactpersoon}
                onChange={(e) => setContactpersoon(e.target.value)}
                fullWidth
              />
              <TextField
                label="Telefoonnummer"
                value={telefoon}
                onChange={(e) => setTelefoon(e.target.value)}
                fullWidth
              />
              <TextField
                label="Opmerkingen"
                value={opmerkingen}
                onChange={(e) => setOpmerkingen(e.target.value)}
                multiline
                rows={2}
                fullWidth
                inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
              />
              <TextField
                label="Parkeerinstructies"
                value={parkeren}
                onChange={(e) => setParkeren(e.target.value)}
                multiline
                rows={2}
                fullWidth
                placeholder="bijv. parkeren op P2, aanmelden bij receptie, of plak een parkeeradres"
                helperText="Verschijnt op de PDF onder de locatie"
                inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
              />
              {parkeerfotoBlock}

              {/* Naam veld onderaan bij handmatige invoer */}
              <Divider />
              <TextField
                label="Naam locatie"
                value={naam}
                onChange={(e) => setNaam(e.target.value)}
                fullWidth
                helperText="Geef een herkenbare naam aan deze locatie"
              />
            </>
          )}

          {/* Extra velden knop alleen bij selecteren */}
          {!useManual && !initialData && selectedLocatie && !showAdvanced && (
            <Button 
              size="small" 
              onClick={() => setShowAdvanced(true)}
              sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
              color="secondary"
            >
              + Extra velden (adres, contactpersoon, etc.)
            </Button>
          )}

          {/* Extra velden bij selecteren */}
          {!useManual && !initialData && showAdvanced && selectedLocatie && (
            <>
              <Divider sx={{ my: 1 }} />
              <TextField
                label="Straat + huisnummer"
                value={straat}
                onChange={(e) => setStraat(e.target.value)}
                fullWidth
              />
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    label="Postcode"
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value)}
                    onBlur={() => { if (!parkeren) lookupParking(straat, postcode, plaats); }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    label="Plaats"
                    value={plaats}
                    onChange={(e) => setPlaats(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
              <TextField
                label="Contactpersoon"
                value={contactpersoon}
                onChange={(e) => setContactpersoon(e.target.value)}
                fullWidth
              />
              <TextField
                label="Telefoonnummer"
                value={telefoon}
                onChange={(e) => setTelefoon(e.target.value)}
                fullWidth
              />
              <TextField
                label="Opmerkingen"
                value={opmerkingen}
                onChange={(e) => setOpmerkingen(e.target.value)}
                multiline
                rows={2}
                fullWidth
                inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
              />
              <TextField
                label="Parkeerinstructies"
                value={parkeren}
                onChange={(e) => setParkeren(e.target.value)}
                multiline
                rows={2}
                fullWidth
                placeholder="bijv. parkeren op P2, aanmelden bij receptie, of plak een parkeeradres"
                helperText="Verschijnt op de PDF onder de locatie"
                inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
              />
              {parkeerfotoBlock}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuleren</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || !naam}>
          {saving ? <CircularProgress size={20} /> : 'Opslaan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Bestandsnaam voor de callsheet-PDF — moet matchen met de backend
// (backend/routes/callsheets.js): "Callsheet - <Titel> - <YYYY-MM-DD>.pdf".
// Nodig omdat een blob-download met a.download het server-Content-Disposition negeert.
// Sinds 29 sep mogen '–', '&' en accenten blijven (backend callsheetPdfNaam, header met filename*).
export function buildPdfFilename(fields = {}) {
  const safeTitle = String(fields.Titel || 'Callsheet')
    .replace(/[\\/:*?"<>|\r\n]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  // Op codepunten afkappen (een emoji niet halveren), gelijk aan de backend.
  const titel = Array.from(safeTitle).slice(0, 80).join('').trim() || 'Naamloos';
  const datumRaw = fields['Datum (handmatig)'] || fields.Datum;
  const datumStr = datumRaw ? String(datumRaw).slice(0, 10) : '';
  return `Callsheet - ${titel}${datumStr ? ` - ${datumStr}` : ''}.pdf`;
}


// ============ MAIN CALLSHEET MANAGER ============
/** Slotjes-cache van deze browser (de server is de bron, zie callsheet_extra.vaste_planning). */
function leesLokaleSloten(callsheetId) {
  try { return new Set(JSON.parse(localStorage.getItem(`callsheet-locked-${callsheetId}`) || '[]')); }
  catch { return new Set(); }
}

function CallSheetManager() {
  const { id } = useParams();
  const fotoApi = useCallsheetFotos(id);   // locatiefoto's (privé blob, SAS-links)
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const skipMissingWarnRef = useRef(false);   // negeer ontbrekende-info melding na 1× bevestigen
  // Staat er een opslagfout? Dan wist herladen die niet; pas de volgende geslaagde opslag (of de
  // gebruiker via ✕) haalt hem weg (Codex r3: de fout verdween meteen door het herstel-herladen).
  const opslagFoutRef = useRef(false);
  // Planningmutaties (Codex r1, 29 sep): een teller die bij elke wijziging ophoogt (zo vervalt
  // "ongedaan maken" van een herberekening zodra er daarna iets verandert) en een slot zodat
  // ▲▼/herberekenen niet overlappen (twee snelle ruilacties verloren anders een begintijd).
  const planningVersieRef = useRef(0);
  const planningGewijzigd = () => { planningVersieRef.current += 1; };
  const planningBezigRef = useRef(false);
  const [planningBezig, setPlanningBezig] = useState(false);
  // Planningfouten blijven staan tijdens het herstel-herladen (fetchCallsheet wist anders de melding)
  // en verdwijnen bij de volgende geslaagde opslag (Codex r2).
  const toonOpslagFout = (melding) => { opslagFoutRef.current = true; setError(melding); };
  const wisOpslagFout = () => { if (opslagFoutRef.current) { opslagFoutRef.current = false; setError(null); } };
  const metPlanningSlot = async (fn) => {
    if (planningBezigRef.current) return false;
    planningBezigRef.current = true; setPlanningBezig(true);
    try { await fn(); return true; } finally { planningBezigRef.current = false; setPlanningBezig(false); }
  };
  useEffect(() => registreerOngedaanSneltoets(), []);   // Ctrl+Z = laatste herberekening terug
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [error, setError] = useState(null);
  const [callsheetData, setCallsheetData] = useState(null);
  const [pdfStatus, setPdfStatus] = useState(null);
  const [pdfPreview, setPdfPreview] = useState({ open: false, url: '', filename: '' }); // in-app PDF-preview
  const [personen, setPersonen] = useState([]);
  const [castingOptions, setCastingOptions] = useState([]);
  const [parking, setParking] = useState({});           // locId → parkeer-resultaat
  const [parkingLoading, setParkingLoading] = useState({});
  const [existingLocaties, setExistingLocaties] = useState([]);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [copyPlanningDialog, setCopyPlanningDialog] = useState(false);
  const [copyPlanningOptions, setCopyPlanningOptions] = useState([]);
  const [copyPlanningSearch, setCopyPlanningSearch] = useState('');
  const [loadingCopyOptions, setLoadingCopyOptions] = useState(false);
  const [clearExistingPlanning, setClearExistingPlanning] = useState(true); // Default: clear existing
  const [copyIncludeCrew, setCopyIncludeCrew] = useState(false);   // crew meekopiëren van bron-callsheet
  const [copyIncludeCast, setCopyIncludeCast] = useState(false);   // cast meekopiëren van bron-callsheet
  const [weather, setWeather] = useState('');
  const [pdfMenu, setPdfMenu] = useState(null);     // anker van het PDF-menu in de kop
  const [meerMenu, setMeerMenu] = useState(null);   // ⋯-menu: dupliceren, vernieuwen, verwijderen

  // 🔒 Vastgezette begintijden per planning-regel — blijven staan bij herberekenen; wat erna
  // komt sluit erop aan (parallel plannen, vaste lunch). Sinds 23 sep op de server
  // (callsheet_extra.vaste_planning): in localStorage zag een collega of ander apparaat ze niet,
  // en de herberekening na opslaan/verwijderen gebruikte een verouderde kopie van de slotjes —
  // Naomi's planning voor de UMC-draaidag sprong daardoor helemaal door.
  const [lockedRows, setLockedRows] = useState(() => leesLokaleSloten(id));
  // Altijd de actuele set: herberekenen gebeurt ook vanuit callbacks die bij het openen zijn gemaakt.
  const lockedRef = useRef(lockedRows);
  useEffect(() => { lockedRef.current = lockedRows; }, [lockedRows]);
  // Welk callsheet nu open is: een laat antwoord voor een vorig callsheet mag de slotjes van
  // het huidige niet overschrijven (Codex-hercheck, 23 sep).
  const huidigIdRef = useRef(id);
  useEffect(() => { huidigIdRef.current = id; }, [id]);
  useEffect(() => {
    const lokaal = leesLokaleSloten(id);
    lockedRef.current = lokaal;
    setLockedRows(lokaal);
  }, [id]);
  const zetSloten = useCallback((lijst) => {
    const set = new Set(lijst || []);
    lockedRef.current = set;
    setLockedRows(set);
    try { localStorage.setItem(`callsheet-locked-${id}`, JSON.stringify([...set])); } catch { /* cache optioneel */ }
  }, [id]);
  // Per klik alleen die ene regel naar de server (vastZetten/vrijGeven), en klikken netjes achter
  // elkaar: zo verliest een snelle tweede klik — of een collega — de eerste niet (Codex-review 23 sep).
  const slotWachtrij = useRef({ keten: Promise.resolve(), open: 0 });
  const bewaarSloten = useCallback(async (wijziging) => {
    const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}/extra`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(wijziging),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || d.detail || `HTTP ${r.status}`);
    return d;
  }, [id]);
  const toggleLock = (rowId) => {
    planningGewijzigd();
    const cid = id;
    const vast = !lockedRef.current.has(rowId);
    const next = new Set(lockedRef.current);
    if (vast) next.add(rowId); else next.delete(rowId);
    lockedRef.current = next;
    setLockedRows(next);
    const w = slotWachtrij.current;
    w.open += 1;
    w.keten = w.keten
      .then(() => bewaarSloten(vast ? { vastZetten: [rowId] } : { vrijGeven: [rowId] }))
      .then((d) => {
        if (huidigIdRef.current === cid && w.open === 1 && Array.isArray(d.vastePlanning)) zetSloten(d.vastePlanning);
      })
      .catch(async (e) => {
        if (huidigIdRef.current !== cid) return;
        setError(`Slotje opslaan mislukt: ${e.message}`);
        try {
          const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${cid}/extra`);
          const d = await r.json();
          if (huidigIdRef.current === cid && Array.isArray(d.vastePlanning)) zetSloten(d.vastePlanning);
        } catch { /* laat de lokale stand staan */ }
      })
      .finally(() => { w.open -= 1; });
  };
  
  // Edit states
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  
  // Dialog states
  const [crewDialog, setCrewDialog] = useState({ open: false, data: null });
  const [castDialog, setCastDialog] = useState({ open: false, data: null });
  const [draggedRow, setDraggedRow] = useState(null); // { key:'crew'|'cast', id }
  const [planningDialog, setPlanningDialog] = useState({ open: false, data: null, standaard: null });
  // Slepen in de planning: welke regel, en waar de invoeglijn staat ({ id, positie: 'boven'|'onder' }).
  const [sleepRegel, setSleepRegel] = useState(null);
  const [dropDoel, setDropDoel] = useState(null);
  const [locatieDialog, setLocatieDialog] = useState({ open: false, data: null });
  // Levend callsheet (Joris, 15 sep): eerst locatie + begin-/eindtijd. Tijden staan in
  // Postgres (callsheet_extra) omdat Airtable die velden niet heeft.
  const [extra, setExtra] = useState({ begintijd: '', eindtijd: '', tijdenBron: null, briefing: '', briefingBronnen: [] });
  // AI-briefing (Joris 18 sep): voorstel uit notities/meetings/mails/dossier → aanvinken → overnemen.
  const [aiBriefing, setAiBriefing] = useState(null);       // { velden, vragen, bronnen, huidig }
  const [aiKeuze, setAiKeuze] = useState({});                // key → { aan, tekst }
  const [briefingTekst, setBriefingTekst] = useState('');
  const [briefingBezig, setBriefingBezig] = useState(false);
  const [briefingBewerk, setBriefingBewerk] = useState(false);
  const [aiTab, setAiTab] = useState(0);                     // 0 = callsheet (productie), 1 = briefing crew
  const [aiAanwijzingen, setAiAanwijzingen] = useState('');
  const [aiExtraBron, setAiExtraBron] = useState('');
  const [aiConflictKeuze, setAiConflictKeuze] = useState({}); // conflict-index → gekozen waarde
  const [aiBronnenOpen, setAiBronnenOpen] = useState(false);
  const [aiBriefingBewerk, setAiBriefingBewerk] = useState(false);   // briefing crew in de dialoog: opgemaakt (links) of ruwe tekst
  const [aiExtraKeuze, setAiExtraKeuze] = useState({});      // locatie/planning/settelefoon/contactKlant uit het voorstel → { aan, … }
  const [bronnenTonen, setBronnenTonen] = useState(true);     // PL ziet bronverwijzingen als links; crew-weergave en PDF niet
  const [briefingActie, setBriefingActie] = useState(null);   // 'pdf' | 'mail' | null
  const { accounts: msalAccounts } = useMsal();
  const AI_VELD_NAAR_AIRTABLE = { algemene_opmerkingen: 'Algemene opmerkingen', props: 'Props', styling: 'Styling', visagie: 'Visagie', lunch: 'Lunch', reminder: 'Reminder', kledingvoorschriften: 'Kledingvoorschriften' };
  const aiAantalAan = Object.values(aiKeuze).filter((v) => v.aan).length + Object.values(aiExtraKeuze).filter((v) => v.aan).length;
  const AI_LABELS = { algemene_opmerkingen: '📝 Algemene opmerkingen', props: '📦 Props', styling: '🎨 Styling', visagie: '💄 Visagie & haar', lunch: '🍴 Lunch / catering', reminder: '⏰ Reminder', kledingvoorschriften: '👕 Kledingvoorschriften', briefing_crew: '🎥 Briefing crew' };
  // AI voorbereiden draait op de achtergrond (tool-feedback #63, Boy 6 okt: "kun je niet cancellen,
  // duurt soms heel lang"). Starten geeft meteen een job; lege velden worden per klaar deel
  // ingevuld, wat al gevuld was (of wat je intussen typte) wordt een voorstel.
  const [aiIngevuld, setAiIngevuld] = useState([]);              // [{ blok, label, herstel, hersteld }]
  const [aiVoorstelBlokken, setAiVoorstelBlokken] = useState([]); // blokken die als voorstel klaarliggen
  const [aiInvulFouten, setAiInvulFouten] = useState([]);
  const editingFieldRef = useRef(null);
  editingFieldRef.current = editingField;
  const briefingLokaalRef = useRef(false);   // briefing open in de editor of nog niet opgeslagen tekst
  briefingLokaalRef.current = briefingBewerk || (!!String(briefingTekst || '').trim() && briefingTekst !== (extra.briefing || ''));
  // Main: 'Algemene opmerkingen' bevat ook het opleveringsblok; dat telt niet als ingevuld en blijft staan.
  const opmerkingenZonderOplevering = (f) => splitsOplevering(f?.['Algemene opmerkingen']).opmerkingen;
  const metOplevering = (nieuw, f) => voegOpleveringSamen(splitsOplevering(nieuw).opmerkingen, splitsOplevering(f?.['Algemene opmerkingen']).oplevering);
  // Welke callsheet nu open is: een late verversing voor een vorige callsheet mag de state van de
  // huidige niet overschrijven (Codex r3).
  const openIdRef = useRef(id);
  openIdRef.current = id;
  const verversAlsNogOpen = async (csId) => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${csId}`);
      if (!r.ok) return;
      const d = await r.json();
      if (openIdRef.current === csId) setCallsheetData(metUniekeCrew(d));
    } catch { /* volgende verversing */ }
  };
  // Na ongedaan maken: alleen de teruggezette blokken bijwerken. Een open of nog niet opgeslagen
  // briefing (of begin-/eindtijd in de editor) blijft altijd staan (Codex r4).
  // `open`: wat open/onopgeslagen stond VÓÓR het terugzetten (dat zelf wijzigt extra.briefing; Codex r5).
  const verversNaHerstel = async (blokken = [], open = []) => {
    const csId = id;
    setAiIngevuld((l) => [...l]);
    if (!blokken.length) return;
    await verversAlsNogOpen(csId);
    if (!blokken.includes('briefing_crew')) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${csId}/extra`);
      const e = r.ok ? await r.json() : null;
      if (!e || openIdRef.current !== csId) return;
      setExtra((x) => ({ ...x, briefing: e.briefing || '', briefingBronnen: e.briefingBronnen || [] }));
      if (naHerstelBijwerken(blokken, open).briefing) setBriefingTekst(e.briefing || '');
    } catch { /* volgende verversing */ }
  };
  // Stil lezen/schrijven (geen foutbanner, geen state): de AI-verwerking kan nog lopen terwijl je al
  // een andere callsheet open hebt; state wordt alleen bijgewerkt als deze callsheet nog open is.
  const haalCallsheetStil = async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}`);
      return r.ok ? await r.json() : null;
    } catch { return null; }
  };
  const haalExtraStil = async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}/extra`);
      return r.ok ? await r.json() : null;
    } catch { return null; }
  };
  const zetExtraUit = (d) => setExtra({ begintijd: d.begintijd || '', eindtijd: d.eindtijd || '', tijdenBron: d.tijdenBron, briefing: d.briefing || '', briefingBronnen: d.briefingBronnen || [] });
  const aiBewerkt = () => [blokVanVeld(editingFieldRef.current), briefingLokaalRef.current ? 'briefing_crew' : null].filter(Boolean);
  // Geeft false terug als het deel (nog) niet goed is ingevuld: dan geeft de hook de claim terug en
  // probeert het later opnieuw; wat al is ingevuld telt dan als gevuld en wordt niet nog eens gedaan.
  const verwerkAiDeel = async (deel, res, job, { actueel } = {}) => {
    const nogHier = () => (!actueel || actueel()) && (!job?.callsheetId || job.callsheetId === id);
    if (!nogHier()) return false;
    const vers = await haalCallsheetStil();
    const versExtra = await haalExtraStil();
    if (!vers || !versExtra || !nogHier()) return false;   // herlezen mislukt of weg: niets op oude gegevens invullen
    const opties = { opmerkingenTekst: opmerkingenZonderOplevering };
    const nu = huidigeBlokken(vers, versExtra, opties);
    const persoon = res.settelefoon ? zoekPersoon(res.settelefoon) : null; // eslint-disable-line no-use-before-define
    const { vullen, overslaan } = kiesInvulling(res.invullen, nu, { bewerkt: aiBewerkt(), persoonGevonden: !!persoon });
    const bewaarExtraStil = async (velden) => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}/extra`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(velden) });
        if (!r.ok) return false;
        const d = await r.json();
        if (nogHier()) zetExtraUit(d);
        return true;
      } catch { return false; }
    };
    const { ingevuld, fouten, overgeslagen } = await vulBlokken(vullen, res, {
      fetcher: authFetch, base: BACKEND_URL, id, extra: versExtra, actueel: nogHier,
      bewaarExtra: bewaarExtraStil, bronnen: job?.resultaat?.bronnen || [], zoekPersoon, haalExtra: haalExtraStil, // eslint-disable-line no-use-before-define
      haalCallsheet: haalCallsheetStil, haalVelden: async () => (await haalCallsheetStil())?.callsheet?.fields,
      controleer: maakControle({ haalCallsheet: haalCallsheetStil, haalExtra: haalExtraStil, opmerkingenTekst: opties.opmerkingenTekst, bewerkt: aiBewerkt }),
      samenvoegOpmerkingen: metOplevering, naPlanning: planningGewijzigd,
    });
    // Bij een herhaalde poging: wat de AI eerder al invulde is geen 'voorstel'.
    const alIngevuld = (b) => !!String(res.velden?.[b] || '').trim() && String(nu[b] || '').trim() === String(res.velden[b]).trim();
    const mislukt = vullen.filter((b) => !ingevuld.some((x) => x.blok === b));
    if (nogHier()) {
      if (ingevuld.some((x) => x.blok === 'briefing_crew')) setBriefingTekst(res.velden.briefing_crew);
      setAiIngevuld((l) => [...l, ...ingevuld]);
      setAiVoorstelBlokken((l) => [...new Set([...l, ...(res.voorstellen || []), ...overslaan.filter((b) => !alIngevuld(b)), ...overgeslagen, ...mislukt])]);
      if (fouten.length) setAiInvulFouten((l) => [...l, ...fouten]);
      biedDeelOngedaanAan(ingevuld, verversNaHerstel, { bewerkt: aiBewerkt });
      if (ingevuld.length) verversAlsNogOpen(id);
    }
    return fouten.length === 0;
  };
  const aiKlaar = (job) => {
    if (!job.opnieuw) return;
    if (job.status === 'klaar') toonVoorstel(voorstelUitJob(job), { opnieuw: true }); // eslint-disable-line no-use-before-define
    else if (job.status === 'fout') toast.error(job.fout || 'AI voorbereiden mislukt');
  };
  const ai = useAiVoorbereiden({ callsheetId: id, fetcher: authFetch, base: BACKEND_URL, onDeel: verwerkAiDeel, onKlaar: aiKlaar });
  const aiBriefingBusy = ai.bezig;
  const aiOpnieuwBezig = ai.bezig && !!ai.job?.opnieuw;
  const herstelAiBlok = async (x) => {
    const open = aiBewerkt();
    try {
      await herstelBlok(x, open);
      toast.success(`${x.label} teruggezet`);
      verversNaHerstel([x.blok], open);
    } catch (e) { toast.error(e.message); setAiIngevuld((l) => [...l]); }
  };
  const aiPunten = ai.job && !ai.job.opnieuw ? ((ai.job.resultaat?.delen?.callsheet?.conflicten || []).length + (ai.job.resultaat?.delen?.callsheet?.vragen || []).length) : 0;
  // Na een refresh staat de lijst niet meer in de state: dan uit de job (wat bij gevulde velden lag).
  const aiVoorstelLijst = aiVoorstelBlokken.length || !ai.job?.hervat ? aiVoorstelBlokken
    : [...new Set(['callsheet', 'briefing'].flatMap((d) => ai.job.resultaat?.delen?.[d]?.voorstellen || []))];
  const bekijkAiVoorstel = () => { if (ai.job) toonVoorstel(voorstelUitJob(ai.job, { alleen: aiVoorstelLijst })); }; // eslint-disable-line no-use-before-define
  // Andere callsheet geopend (zelfde component): AI-samenvatting van de vorige hoort hier niet.
  useEffect(() => { setAiIngevuld([]); setAiVoorstelBlokken([]); setAiInvulFouten([]); }, [id]);

  // Vanuit de aanmaak-wizard (?ai=1): meteen voorbereiden zodra de callsheet geladen is.
  const aiAutoGestart = useRef(false);
  useEffect(() => {
    if (!callsheetData || aiAutoGestart.current || searchParams.get('ai') !== '1') return;
    aiAutoGestart.current = true;
    // ?ai=1 uit de adresbalk: een refresh mag niet nog een voorbereiding starten.
    try { const u = new URL(window.location.href); u.searchParams.delete('ai'); window.history.replaceState(window.history.state, '', u.toString()); } catch { /* geen URL-API */ }
    vraagAiBriefing(); // eslint-disable-line no-use-before-define
  }, [callsheetData, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const vraagAiBriefing = async ({ opnieuw = false } = {}) => {
    try {
      if (opnieuw) {
        const keuzes = Object.entries(aiConflictKeuze).map(([i, w]) => `${aiBriefing?.conflicten?.[i]?.onderwerp || 'keuze'}: ${w}`);
        await ai.start({ opnieuw: true, aanwijzingen: aiAanwijzingen, extraBron: aiExtraBron, keuzes, vorigVoorstel: { velden: Object.fromEntries(Object.entries(aiKeuze).map(([k, v]) => [k, v.tekst])) } });
        return;
      }
      setAiIngevuld([]); setAiVoorstelBlokken([]); setAiInvulFouten([]);
      await ai.start({ leegBijStart: leegBijStart(huidigeBlokken(callsheetData, extra, { opmerkingenTekst: opmerkingenZonderOplevering })) });
    } catch (e) { toast.error(e.message); }
  };
  // Het voorstel in de dialoog: alleen wat nog niet automatisch is ingevuld (of een herhaalde vraag).
  const toonVoorstel = (d, { opnieuw = false } = {}) => {
    const heeftIets = Object.keys(d.velden || {}).length || d.locatie || (d.planning || []).length || d.settelefoon || d.contactKlant
      || (d.conflicten || []).length || (d.vragen || []).length;
    if (!heeftIets) { toast(d.melding || 'Geen voorstel: er zijn nog geen bronnen aan dit project gekoppeld.'); return; }
    const nu = huidigeBlokken(callsheetData, extra, { opmerkingenTekst: opmerkingenZonderOplevering });
    const keuze = {};
    Object.entries(d.velden || {}).forEach(([k, tekst]) => {
      const bestaand = nu[k] || '';
      const vorige = aiKeuze[k];
      keuze[k] = { aan: opnieuw && vorige ? vorige.aan : !String(bestaand).trim(), tekst, bestaand };
    });
    const ck = {};
    (d.conflicten || []).forEach((c, i) => { ck[i] = c.gekozen || c.versies?.[0]?.waarde || ''; });
    const cf = callsheetData?.callsheet?.fields || {};
    const ek = {};
    if (d.locatie) ek.locatie = { aan: !(callsheetData?.locaties || []).length, ...d.locatie };
    if ((d.planning || []).length) ek.planning = { aan: !(callsheetData?.planning || []).length, rows: d.planning, vervang: false };
    if (d.settelefoon) { const p = zoekPersoon(d.settelefoon); ek.settelefoon = { aan: !!p && !cf['Naam (from Contactpersoon Settelefoon)']?.[0], naam: d.settelefoon, persoon: p }; } // eslint-disable-line no-use-before-define
    if (d.contactKlant) ek.contactKlant = { aan: !(cf['Contactpersoon (handmatig)'] || cf['Contactgegevens klant telefoon (Handmatig)']), ...d.contactKlant };
    setAiExtraKeuze(ek);
    setAiConflictKeuze(ck); setAiKeuze(keuze); setAiBriefing(d); setAiTab(0);
    if (!opnieuw) { setAiAanwijzingen(''); setAiExtraBron(''); }
  };
  const neemAiOver = async () => {
    const gekozen = Object.entries(aiKeuze).filter(([, v]) => v.aan && String(v.tekst || '').trim());
    const ek = aiExtraKeuze;
    const extraAan = ['locatie', 'planning', 'settelefoon', 'contactKlant'].filter((k) => ek[k]?.aan);
    if (!gekozen.length && !extraAan.length) { setAiBriefing(null); return; }
    const velden = {};
    let briefing = null;
    gekozen.forEach(([k, v]) => { if (k === 'briefing_crew') briefing = v.tekst; else if (AI_VELD_NAAR_AIRTABLE[k]) velden[AI_VELD_NAAR_AIRTABLE[k]] = v.tekst; });
    // Het oplevering-blok (📦 Deliverables) staat in hetzelfde Airtable-veld; een AI-tekst voor de
    // opmerkingen mag dat blok niet wegvegen.
    if (velden['Algemene opmerkingen'] != null) {
      const { oplevering } = splitsOplevering(callsheetData?.callsheet?.fields?.['Algemene opmerkingen']);
      velden['Algemene opmerkingen'] = voegOpleveringSamen(splitsOplevering(velden['Algemene opmerkingen']).opmerkingen, oplevering);
    }
    if (ek.settelefoon?.aan && ek.settelefoon.persoon) velden['Contactpersoon Settelefoon'] = [ek.settelefoon.persoon.id];
    if (ek.contactKlant?.aan) {
      if (ek.contactKlant.naam) velden['Contactpersoon (handmatig)'] = ek.contactKlant.naam;
      if (ek.contactKlant.telefoon) velden['Contactgegevens klant telefoon (Handmatig)'] = ek.contactKlant.telefoon;
    }
    const json = { 'Content-Type': 'application/json' };
    let planningWaarschuwing = null;
    try {
      if (Object.keys(velden).length) {
        const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}`, { method: 'PATCH', headers: json, body: JSON.stringify(velden) });
        if (!r.ok) throw new Error(`Velden opslaan mislukt (HTTP ${r.status})`);
      }
      if (briefing !== null) { if (!(await bewaarExtra({ briefing, briefingBronnen: aiBriefing?.bronnen || [] }))) throw new Error('Briefing opslaan mislukt'); setBriefingTekst(briefing); }
      if (ek.locatie?.aan) {
        const r = await authFetch(`${BACKEND_URL}/Api/callsheet-locaties`, { method: 'POST', headers: json, body: JSON.stringify({ callsheetId: id, naam: ek.locatie.naam, straat: ek.locatie.straat, postcode: ek.locatie.postcode, plaats: ek.locatie.plaats, opmerkingen: ek.locatie.opmerkingen }) });
        if (!r.ok) throw new Error(`Locatie toevoegen mislukt (HTTP ${r.status})`);
      }
      if (ek.planning?.aan) {
        planningGewijzigd();
        // Eerst de nieuwe regels, dan pas de oude weg; mislukt een POST halverwege, dan halen we de
        // net aangemaakte regels weer weg zodat opnieuw proberen geen duplicaten geeft (Codex #4).
        const oude = ek.planning.vervang ? (callsheetData?.planning || []).map((p) => p.id) : [];
        const nieuw = [];
        try {
          for (const row of ek.planning.rows) {
            const [h, mnt] = String(row.tijd).split(':').map(Number);
            if (!(h >= 0 && h <= 23 && mnt >= 0 && mnt <= 59)) continue;
            const r = await authFetch(`${BACKEND_URL}/Api/callsheet-planning`, { method: 'POST', headers: json, body: JSON.stringify({ callsheetId: id, tijd: h * 3600 + mnt * 60, duur: row.duur_min || 0, omschrijving: row.omschrijving || '', locatie: row.locatie || '' }) });
            if (!r.ok) throw new Error(`Planningsregel toevoegen mislukt (HTTP ${r.status})`);
            const d = await r.json().catch(() => ({}));
            const nid = d?.record?.id || d?.id;
            if (nid) nieuw.push(nid);
          }
        } catch (e) {
          for (const nid of nieuw) await authFetch(`${BACKEND_URL}/Api/callsheet-planning/${nid}`, { method: 'DELETE' }).catch(() => {});
          fetchCallsheet(false);
          throw e;
        }
        let nietWeg = 0;
        for (const pid of oude) {
          const r = await authFetch(`${BACKEND_URL}/Api/callsheet-planning/${pid}`, { method: 'DELETE' });
          if (!r.ok) nietWeg += 1;
        }
        if (nietWeg) planningWaarschuwing = `${nietWeg} oude planningsregel${nietWeg === 1 ? '' : 's'} kon niet worden verwijderd — controleer de planning`;
      }
      setAiVoorstelBlokken((l) => l.filter((b) => !gekozen.some(([k]) => k === b) && !extraAan.includes(b)));
      const n = gekozen.length + extraAan.length;
      if (planningWaarschuwing) toast(`${n} onderdeel${n === 1 ? '' : 'en'} overgenomen, maar: ${planningWaarschuwing}`, { icon: '⚠️', duration: 8000 });
      else toast.success(`${n} onderdeel${n === 1 ? '' : 'en'} overgenomen`);
      setAiBriefing(null);
      fetchCallsheet(false);
    } catch (e) { toast.error(e.message); }
  };
  const haalBriefingPdf = async () => {
    const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}/briefing.pdf`);
    if (!r.ok) throw new Error(`PDF maken mislukt (HTTP ${r.status})`);
    return r.blob();
  };
  const briefingBestandsnaam = () => `Briefing ${String(callsheetData?.callsheet?.fields?.Titel || 'draaidag').replace(/[\\/:*?"<>|]+/g, ' ').trim()}.pdf`;
  const downloadBriefingPdf = async () => {
    setBriefingActie('pdf');
    try {
      const url = URL.createObjectURL(await haalBriefingPdf());
      const a = document.createElement('a'); a.href = url; a.download = briefingBestandsnaam(); document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(e.message); }
    finally { setBriefingActie(null); }
  };
  // Concept in Outlook (map Concepten) aan de crew, met de briefing-PDF als bijlage.
  // Draft-only: de PL leest na en verstuurt zelf (Joris 18 sep).
  const conceptMailCrew = async () => {
    setBriefingActie('mail');
    try {
      const [blob, ontvR] = await Promise.all([
        haalBriefingPdf(),
        authFetch(`${BACKEND_URL}/Api/callsheets/${id}/crew-ontvangers`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }),
      ]);
      const ontv = ontvR.ok ? await ontvR.json() : { ontvangers: [], onbekend: [] };
      const base64 = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(String(fr.result).split(',')[1]); fr.onerror = rej; fr.readAsDataURL(blob); });
      const f = callsheetData?.callsheet?.fields || {};
      const titel = f.Titel || 'de draaidag';
      const datum = f['Datum (handmatig)'] || f.Datum || '';
      const voornamen = (ontv.ontvangers || []).map((o) => String(o.naam || '').split(' ')[0]).filter(Boolean);
      const mijnNaam = String(msalAccounts?.[0]?.name || '').split(' ')[0];
      const body = [
        `Hoi ${voornamen.length ? voornamen.join(', ') : 'allemaal'},`, '',
        `In de bijlage de briefing voor ${titel}${datum ? ` op ${datum}` : ''}: het verhaal achter de film, wat er zeker gefilmd moet worden en waar je op moet letten.`, '',
        `Het callsheet met tijden, locatie en contactgegevens staat hier: ${window.location.origin}/callsheets/${id}`, '',
        'Vragen? Bel of app me gerust.', '', 'Groet,', mijnNaam,
      ].join('\n');
      const d = await createDraftMessage({
        subject: `Briefing ${titel}${datum ? ` – ${datum}` : ''}`, body,
        to: (ontv.ontvangers || []).map((o) => o.email), attachments: [{ name: briefingBestandsnaam(), base64 }],
      });
      const onbekend = (ontv.onbekend || []).length ? ` Geen e-mail bekend van: ${ontv.onbekend.join(', ')}.` : '';
      toast.success((t) => (
        <span>Concept staat in Outlook ({(ontv.ontvangers || []).length} ontvangers).{onbekend}{' '}
          {d.webLink && <a href={d.webLink} target="_blank" rel="noreferrer" onClick={() => toast.dismiss(t.id)}>Openen</a>}</span>
      ), { duration: 10000 });
    } catch (e) { toast.error(e.message); }
    finally { setBriefingActie(null); }
  };
  // Briefing → Script breakdown / shotlist-planner als nieuw script (zonder bronverwijzingen).
  const naarShotlist = () => {
    const f = callsheetData?.callsheet?.fields || {};
    const pnRaw = callsheetData?.draaidag?.[0]?.fields?.['Projectnummer (from Project)'];
    const pn = Array.isArray(pnRaw) ? pnRaw[0] : pnRaw;
    navigate('/script-breakdown', { state: { prefill: { title: `Shotlist ${f.Titel || 'draaidag'}`, script: stripBronRefs(briefingTekst), projectNumber: pn != null ? String(pn) : '' } } });
  };
  // Settelefoon uit het AI-voorstel koppelen aan een Personen-record (exacte naam, anders unieke voornaam).
  const zoekPersoon = (naam) => {
    const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim();
    const n = norm(naam);
    if (!n) return null;
    const lijst = (personen || []).filter((p) => !p._source);
    const naamVan = (p) => norm(p.fields?.['Volledige naam'] || p.fields?.Naam);
    const exact = lijst.find((p) => naamVan(p) === n);
    if (exact) return exact;
    const kandidaten = lijst.filter((p) => naamVan(p).split(' ')[0] === n.split(' ')[0]);
    return kandidaten.length === 1 ? kandidaten[0] : null;
  };
  useEffect(() => {
    if (!id) return undefined;
    let actief = true;
    authFetch(`${BACKEND_URL}/Api/callsheets/${id}/extra`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!actief || !d) return;
        setExtra({ begintijd: d.begintijd || '', eindtijd: d.eindtijd || '', tijdenBron: d.tijdenBron, briefing: d.briefing || '', briefingBronnen: d.briefingBronnen || [] });
        setBriefingTekst(d.briefing || '');
        // Slotjes: de server is de bron. Staan er alleen nog oude slotjes in deze browser, dan gaan
        // die eenmalig naar de server, zodat collega's en andere apparaten ze ook zien.
        // Heeft de server ooit slotjes gehad (ook een bewust lege set), dan is hij leidend; alleen als
        // hij nog nooit iets kreeg gaan oude slotjes uit deze browser één keer mee.
        const lokaal = leesLokaleSloten(id);
        if (d.vastePlanningGezet || !lokaal.size) {
          zetSloten(Array.isArray(d.vastePlanning) ? d.vastePlanning : []);
        } else {
          bewaarSloten({ vastZetten: [...lokaal], migratie: true })
            .then((uit) => { if (actief && Array.isArray(uit.vastePlanning)) zetSloten(uit.vastePlanning); })
            .catch(() => {});
        }
      })
      .catch(() => {});
    return () => { actief = false; };
  }, [id, bewaarSloten, zetSloten]);
  // Crew informeren (Joris, 15 sep): melding in intern + update in de crew-app standaard aan,
  // e-mail standaard uit. De PL kiest per keer.
  const [informeer, setInformeer] = useState(null);   // null | { bericht, webapp, crewApp, mail, bezig }
  // Bijlagen bij de crew-mail (21 okt): overzicht uit het blok, ook getoond in het venster.
  const [bijlagenOverzicht, setBijlagenOverzicht] = useState(null);
  // Dubbelcheck vóór versturen (Joris, 3 nov): "Versturen" opent eerst de checklist; pas na
  // "Klopt, versturen" gaat het bestaande vangnet (5 s Annuleren bij e-mail) lopen.
  const [verzendCheck, setVerzendCheck] = useState(false);
  const laadCrewOntvangers = useCallback(async () => {
    const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}/crew-ontvangers`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  }, [id]);
  const verstuurInformeer = async (bevestigd) => {
    if (!informeer || informeer.bezig) return;
    const keuze = informeer;
    setInformeer((x) => ({ ...x, bezig: true }));
    // Wie de PL in de checklist zag, krijgt het: vlak vóór het echte verzoek (ook na de 5 s)
    // nog eens ophalen; anders veranderd of niet vast te stellen → niets versturen.
    const vooraf = async () => {
      let nu;
      try { nu = await laadCrewOntvangers(); } catch { throw new Error('Ontvangers niet te controleren — er is niets verstuurd'); }
      if (!zelfdeOntvangers(nu, bevestigd?.ontvangers)) throw new Error('De ontvangers zijn intussen veranderd — er is niets verstuurd. Controleer de checklist opnieuw.');
    };
    const verwachteOntvangers = (bevestigd?.ontvangers?.ontvangers || []).map((o) => o.email).filter(Boolean);
    const r = await informeerCrew({ callsheetId: id, keuze, overzicht: bijlagenOverzicht, onStart: () => setInformeer(null), vooraf, verwachteOntvangers });
    if (r.ok) fetchCallsheet(false);
    else setInformeer({ ...keuze, bezig: false });
  };
  const bewaarExtra = async (velden) => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}/extra`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(velden),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setExtra({ begintijd: d.begintijd || '', eindtijd: d.eindtijd || '', tijdenBron: d.tijdenBron, briefing: d.briefing || '', briefingBronnen: d.briefingBronnen || [] });
      return true;
    } catch (e) { toast.error(`Opslaan mislukt: ${e.message}`); return false; }
  };
  
  // Fetch personen (team members) for crew selection — /crew bevat ook freelancers/
  // stagiairs (externen), niet alleen interne medewerkers.
  const fetchPersonen = useCallback(async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/personen/crew`);
      if (response.ok) {
        const data = await response.json();
        // API returns { people, records } - use records for Autocomplete (has fields property)
        setPersonen(data.records || []);
      }
    } catch (err) {
      console.error('Error fetching personen:', err);
    }
  }, []);
  
  // Fetch casting options for cast selection
  const fetchCasting = useCallback(async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/casting`);
      if (response.ok) {
        const data = await response.json();
        setCastingOptions(data || []);
      }
    } catch (err) {
      console.error('Error fetching casting:', err);
    }
  }, []);
  
  // Fetch existing locations for location selection
  const fetchLocaties = useCallback(async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/locaties`);
      if (response.ok) {
        const data = await response.json();
        setExistingLocaties(data || []);
      }
    } catch (err) {
      console.error('Error fetching locaties:', err);
    }
  }, []);
  
  // Fetch callsheet data (with loading state)
  const fetchCallsheet = useCallback(async (showLoading = true) => {
    if (!id) return;
    
    if (showLoading) {
      setLoading(true);
    }
    if (!opslagFoutRef.current) setError(null);
    
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}`);
      if (!response.ok) throw new Error('Callsheet niet gevonden');
      
      const data = metUniekeCrew(await response.json());
      setCallsheetData(data);
      setPdfStatus(data.callsheet?.fields?.StatusPDFWarning || data.callsheet?.fields?.StatusPDF);
      return data;
    } catch (err) {
      setError(err.message);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [id]);
  
  // Soft refresh - update data without showing loading spinner
  const refreshData = useCallback(() => {
    fetchCallsheet(false);
  }, [fetchCallsheet]);

  
  useEffect(() => {
    fetchCallsheet(true);
    fetchPersonen();
    fetchCasting();
    fetchLocaties();
    // Fetch weather in background
    if (id) {
      authFetch(`${BACKEND_URL}/Api/callsheets/${id}/weather`)
        .then(r => r.ok ? r.json() : { weather: '' })
        .then(d => setWeather(d.weather || ''))
        .catch(() => {});
    }
  }, [fetchCallsheet, fetchPersonen, fetchCasting, fetchLocaties, id]);

  // Net aangemaakt (?fresh=1): de standaard-planning wordt async door een Airtable-
  // automation toegevoegd. Doe een paar stille her-fetches zodat die vanzelf verschijnt
  // (geen handmatige refresh nodig).
  useEffect(() => {
    if (!id || searchParams.get('fresh') !== '1') return;
    const timers = [2500, 5000, 8000].map(ms => setTimeout(() => fetchCallsheet(false), ms));
    return () => timers.forEach(clearTimeout);
  }, [id, searchParams, fetchCallsheet]);

  // Update callsheet field
  // Optimistisch alleen dit veld bijwerken — geen volledige paginarefresh.
  const updateField = async (fieldName, value) => {
    setEditingField(null);
    return patchVelden({ [fieldName]: value });   // zelfde opslag- en foutafhandeling
  };
  
  // Delete crew member
  const deleteCrew = async (crewId) => {
    if (!(await bevestig({ titel: 'Crewlid verwijderen?', tekst: 'Van deze callsheet. Dit is niet ongedaan te maken.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      await authFetch(`${BACKEND_URL}/Api/callsheet-crew/${crewId}`, { method: 'DELETE' });
      await verwijderCrewDubbelen((callsheetData?.crew || []).find((c) => c.id === crewId)?._dubbelen);
      fetchCallsheet(false);
    } catch (err) {
      await fetchCallsheet(false);   // toon wat er wél is gebeurd; die verversing wist de fout, dus daarna zetten
      setError(err.message);
    }
  };

  // Zet de calltijd van alle crew gelijk aan de eerste call-tijd van de dag (eerste
  // planning-item op tijd). Daarna kan per crewlid de tijd nog los aangepast worden.
  const setAllCrewToFirstCalltime = async () => {
    const planningItems = callsheetData?.planning || [];
    const crewItems = callsheetData?.crew || [];
    if (!planningItems.length) { setError('Geen planning — voeg eerst een eerste call-tijd toe'); return; }
    if (!crewItems.length) return;
    const firstTime = [...planningItems].sort((a, b) => (a.fields?.Tijd || 0) - (b.fields?.Tijd || 0))[0]?.fields?.Tijd;
    if (firstTime == null) return;
    if (!(await bevestig({ titel: 'Calltijd voor alle crew gelijkzetten?', tekst: `Alle ${crewItems.length} crewleden krijgen ${formatDuration(firstTime)}. Je kunt daarna per persoon aanpassen.`, knop: 'Gelijkzetten' }))) return;
    // Optimistisch
    setCallsheetData(prev => prev ? ({
      ...prev,
      crew: (prev.crew || []).map(c => ({ ...c, fields: { ...c.fields, Calltijd: firstTime } })),
    }) : prev);
    try {
      await Promise.all(crewItems.map(c =>
        authFetch(`${BACKEND_URL}/Api/callsheet-crew/${c.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ Calltijd: firstTime }),
        })
      ));
    } catch (err) {
      fetchCallsheet(false);
      setError(err.message);
    }
  };

  // Zelfde als crew, maar voor cast: zet alle cast op de eerste call-tijd van de dag.
  const setAllCastToFirstCalltime = async () => {
    const planningItems = callsheetData?.planning || [];
    const castItems = callsheetData?.cast || [];
    if (!planningItems.length) { setError('Geen planning — voeg eerst een eerste call-tijd toe'); return; }
    if (!castItems.length) return;
    const firstTime = [...planningItems].sort((a, b) => (a.fields?.Tijd || 0) - (b.fields?.Tijd || 0))[0]?.fields?.Tijd;
    if (firstTime == null) return;
    if (!(await bevestig({ titel: 'Calltijd voor alle cast gelijkzetten?', tekst: `Alle ${castItems.length} cast krijgen ${formatDuration(firstTime)}. Je kunt daarna per persoon aanpassen.`, knop: 'Gelijkzetten' }))) return;
    setCallsheetData(prev => prev ? ({
      ...prev,
      cast: (prev.cast || []).map(c => ({ ...c, fields: { ...c.fields, Calltijd: firstTime } })),
    }) : prev);
    try {
      await Promise.all(castItems.map(c =>
        authFetch(`${BACKEND_URL}/Api/callsheet-cast/${c.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ Calltijd: firstTime }),
        })
      ));
    } catch (err) {
      fetchCallsheet(false);
      setError(err.message);
    }
  };

  // Sorteer crew/cast op handmatige Volgorde (slepen); val terug op calltijd zolang
  // er nog geen volgorde is gezet. Volgorde leeft op het callsheet-koppelrecord, dus
  // per callsheet onafhankelijk (een persoon in meerdere callsheets botst niet).
  const sortByVolgorde = (rows) => [...(rows || [])].sort((a, b) => {
    const va = a.fields?.Volgorde ?? Infinity;
    const vb = b.fields?.Volgorde ?? Infinity;
    if (va !== vb) return va - vb;
    const ca = a.fields?.Calltijd ?? Infinity, cb = b.fields?.Calltijd ?? Infinity;
    if (ca !== cb) return ca - cb;
    // Stabiel: aanmaakvolgorde als laatste tie-break, anders husselt de lijst.
    return String(a.createdTime || '').localeCompare(String(b.createdTime || ''));
  });

  // Sla een nieuwe volgorde op: hernummer Volgorde 0..n en PATCH elke rij.
  const persistRowOrder = async (orderedRows, key, endpoint) => {
    setCallsheetData(prev => prev ? ({
      ...prev,
      [key]: orderedRows.map((r, i) => ({ ...r, fields: { ...r.fields, Volgorde: i } })),
    }) : prev);
    try {
      await Promise.all(orderedRows.map((r, i) =>
        authFetch(`${BACKEND_URL}/Api/${endpoint}/${r.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ Volgorde: i }),
        })
      ));
    } catch (err) {
      fetchCallsheet(false);
      setError(err.message);
    }
  };

  // Drop-handler voor slepen: verplaats de gesleepte rij naar de positie van targetId.
  const handleRowDrop = (key, endpoint, sortedRows, targetId) => {
    if (!draggedRow || draggedRow.key !== key) { setDraggedRow(null); return; }
    const fromIdx = sortedRows.findIndex(r => r.id === draggedRow.id);
    const toIdx = sortedRows.findIndex(r => r.id === targetId);
    setDraggedRow(null);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
    const arr = [...sortedRows];
    const [moved] = arr.splice(fromIdx, 1);
    arr.splice(toIdx, 0, moved);
    persistRowOrder(arr, key, endpoint);
  };

  // Delete planning item
  // Dupliceer een planning-regel (zelfde velden, nieuwe rij).
  const duplicatePlanning = async (p) => {
    planningGewijzigd();
    try {
      const f = p.fields || {};
      await authFetch(`${BACKEND_URL}/Api/callsheet-planning`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          callsheetId: id,
          tijd: f.Tijd || 0, duur: f['Duur (minuten)'] || 0,
          omschrijving: f.Omschrijving || '', locatie: f.Locatie || '',
          cast: f.Cast || '', opmerkingen: f.Opmerkingen || '',
        }),
      });
      await refreshData();
    } catch (err) { setError(err.message); }
  };

  const deletePlanning = async (planningId) => {
    if (!(await bevestig({ titel: 'Planning-item verwijderen?', knop: 'Verwijderen', gevaarlijk: true }))) return;
    planningGewijzigd();
    try {
      // Optimistically remove from UI to avoid full refresh
      setCallsheetData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          planning: (prev.planning || []).filter(p => p.id !== planningId)
        };
      });
      await authFetch(`${BACKEND_URL}/Api/callsheet-planning/${planningId}`, { method: 'DELETE' });
      // Geen herberekening: de andere regels houden hun tijden (Joris 29 sep, TIVO schoof uren op).
      await refreshData();
    } catch (err) {
      // Rollback by reloading if something failed
      refreshData();
      setError(err.message);
    }
  };

  // Inline bewerken van planning-cellen (Omschrijving / Duur / Opmerkingen).
  const [editCell, setEditCell] = useState(null); // { id, field }
  const [editVal, setEditVal] = useState('');

  const beginEditCell = (p, field) => {
    setEditCell({ id: p.id, field });
    // Tijd staat als seconden in de DB maar tonen/bewerken we als HH:MM.
    setEditVal(field === 'Tijd' ? formatDuration(p.fields?.Tijd) : String(p.fields?.[field] ?? ''));
  };

  const savePlanningCell = async () => {
    if (!editCell) return;
    planningGewijzigd();
    const { id, field } = editCell;
    const value = field === 'Duur (minuten)' ? (parseInt(editVal, 10) || 0)
      : field === 'Tijd' ? parseDuration(editVal)
      : editVal;
    setEditCell(null);
    // Optimistische update
    setCallsheetData(prev => prev ? ({
      ...prev,
      planning: (prev.planning || []).map(p => p.id === id ? { ...p, fields: { ...p.fields, [field]: value } } : p),
    }) : prev);
    try {
      await authFetch(`${BACKEND_URL}/Api/callsheet-planning/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
    } catch (err) {
      refreshData();
      setError(err.message);
    }
  };

  // Render een planning-cel: klik → inline TextField, opslaan bij blur/Enter, Esc = annuleren.
  // Direct een planning-veld zetten (bv. Locatie-dropdown), optimistisch + PATCH.
  const setPlanningField = async (planningId, field, value) => {
    planningGewijzigd();
    setCallsheetData(prev => prev ? ({
      ...prev,
      planning: (prev.planning || []).map(p => p.id === planningId ? { ...p, fields: { ...p.fields, [field]: value } } : p),
    }) : prev);
    try {
      await authFetch(`${BACKEND_URL}/Api/callsheet-planning/${planningId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
    } catch (err) { refreshData(); setError(err.message); }
  };

  const renderPlanningCell = (p, field, display) => {
    const editing = editCell && editCell.id === p.id && editCell.field === field;
    if (editing) {
      return (
        <TextField
          value={editVal}
          autoFocus
          variant="standard"
          size="small"
          type={field === 'Duur (minuten)' ? 'number' : 'text'}
          onChange={(e) => setEditVal(e.target.value)}
          onBlur={savePlanningCell}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); savePlanningCell(); }
            if (e.key === 'Escape') setEditCell(null);
          }}
          sx={{ minWidth: field === 'Omschrijving' ? 160 : 70 }}
        />
      );
    }
    return (
      <Box
        onClick={() => beginEditCell(p, field)}
        title="Klik om te bewerken"
        sx={{ cursor: 'text', minHeight: 22, px: 0.5, borderRadius: 0.5, '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' } }}
      >
        {display}
      </Box>
    );
  };

  // ▲▼: ruil alleen de twee betrokken regels (A krijgt de begintijd van B en omgekeerd); de rest
  // blijft staan. Eerder herketende dit ALLE regels op duur, waardoor parallelle onderdelen
  // (expositie naast het programma) uren opschoven (Joris 29 sep, TIVO). Een vaste regel
  // (slotje) ruilt niet mee.
  const buurVan = (itemId, direction) => {
    const gesorteerd = [...(planning || [])].sort((a, b) => (a.fields?.Tijd || 0) - (b.fields?.Tijd || 0));
    const i = gesorteerd.findIndex((p) => p.id === itemId);
    const j = direction === 'up' ? i - 1 : i + 1;
    if (i < 0 || j < 0 || j >= gesorteerd.length) return null;
    return { item: gesorteerd[i], buur: gesorteerd[j] };
  };
  const movePlanningItem = (itemId, direction) => metPlanningSlot(async () => {
    const paar = buurVan(itemId, direction);
    if (!paar) return;
    const { item, buur } = paar;
    if (lockedRef.current.has(item.id) || lockedRef.current.has(buur.id)) {
      toast('Een regel met een vaste begintijd (slotje) ruilt niet mee. Geef hem eerst vrij.', { icon: '🔒' });
      return;
    }
    const tA = item.fields?.Tijd || 0;
    const tB = buur.fields?.Tijd || 0;
    if (tA === tB) return;   // gelijktijdig (parallel): er valt niets te ruilen
    planningGewijzigd();
    const updates = [{ id: item.id, tijd: tB }, { id: buur.id, tijd: tA }];
    setCallsheetData((prev) => prev ? ({
      ...prev,
      planning: (prev.planning || []).map((p) => {
        const u = updates.find((x) => x.id === p.id);
        return u ? { ...p, fields: { ...p.fields, Tijd: u.tijd } } : p;
      }),
    }) : prev);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/callsheet-planning/batch-update-times`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      wisOpslagFout();
    } catch (err) {
      toonOpslagFout('Verplaatsen mislukt: ' + err.message);
    }
    // Pas na bevestigde opslag én verse data mag de volgende ruil (die rekent dan op de echte tijden).
    await fetchCallsheet(false);
  });

  // Slepen (Joris 29 sep): de versleepte regel krijgt als begintijd het einde van de regel erboven
  // (bovenaan: eerste tijd − eigen duur). Alleen die regel verandert; vaste regels slepen niet.
  const verplaatsNaar = (regelId, doelIndex) => metPlanningSlot(async () => {
    const gesorteerd = sorteerPlanning(planning);
    const regel = gesorteerd.find((p) => p.id === regelId);
    if (!regel) return;
    if (lockedRef.current.has(regelId)) {
      toast('Een regel met een vaste begintijd (slotje) versleep je niet. Geef hem eerst vrij.', { icon: '🔒' });
      return;
    }
    const oudeTijd = regel.fields?.Tijd || 0;
    const nieuweTijd = tijdNaVerslepen(gesorteerd, regelId, doelIndex);
    if (nieuweTijd === oudeTijd) return;
    const updates = [{ id: regelId, tijd: nieuweTijd }];
    // Undo-snapshot vóór de request: wie tijdens het opslaan iets wijzigt (bv. een slotje zet),
    // maakt ongedaan maken ongeldig (Codex r1).
    planningGewijzigd();
    const versie = planningVersieRef.current;
    const slotenNa = [...lockedRef.current].sort().join(',');
    setCallsheetData((prev) => prev ? ({
      ...prev,
      planning: (prev.planning || []).map((p) => (p.id === regelId ? { ...p, fields: { ...p.fields, Tijd: nieuweTijd } } : p)),
    }) : prev);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/callsheet-planning/batch-update-times`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      wisOpslagFout();
    } catch (err) {
      toonOpslagFout('Verplaatsen mislukt: ' + err.message);
      await fetchCallsheet(false);
      return;
    }
    await fetchCallsheet(false);
    biedTijdenOngedaanAan(`${regel.fields?.Omschrijving || 'Regel'} verplaatst naar ${formatDuration(nieuweTijd)}`,
      updates, [{ id: regelId, tijd: oudeTijd }], versie, slotenNa);
  });
  const nieuweRegel = (index) => {
    const gesorteerd = sorteerPlanning(planning);
    setPlanningDialog({ open: true, data: null, standaard: { tijd: tijdVoorInvoegen(gesorteerd, index), duur: STANDAARD_DUUR_MIN } });
  };

  // "Ongedaan maken" voor tijdwijzigingen (herberekenen, verslepen). Alleen terugzetten als er
  // sindsdien niets is veranderd (Codex r1/r2): geen planningmutatie hier (ook niet tíjdens het
  // ophalen), dezelfde slotjes — lokaal én op de server, want een collega kan er een gezet hebben —
  // en de verse tijden zijn nog exact de nieuwe. Het herstel loopt achter het planningslot.
  const biedTijdenOngedaanAan = (tekst, updates, oud, versie, slotenNa) => {
    biedOngedaanAan(tekst, async () => {
      const kanNietMeer = 'er is sindsdien iets gewijzigd — ongedaan maken kan niet meer';
      let fout = null;
      const gelopen = await metPlanningSlot(async () => {
        const gewijzigd = () => planningVersieRef.current !== versie || [...lockedRef.current].sort().join(',') !== slotenNa;
        if (gewijzigd()) { fout = kanNietMeer; return; }
        const [vers, ex] = await Promise.all([
          fetchCallsheet(false),
          authFetch(`${BACKEND_URL}/Api/callsheets/${id}/extra`).then((r) => (r.ok ? r.json() : null)).catch(() => null),
        ]);
        const nu = new Map((vers?.planning || []).map((p) => [p.id, p.fields?.Tijd || 0]));
        const serverSloten = ex ? (Array.isArray(ex.vastePlanning) ? [...ex.vastePlanning] : []).sort().join(',') : null;
        if (gewijzigd() || !vers || serverSloten !== slotenNa || !updates.every((u) => nu.get(u.id) === u.tijd)) {
          fout = kanNietMeer; return;
        }
        const r = await authFetch(`${BACKEND_URL}/Api/callsheet-planning/batch-update-times`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates: oud }),
        });
        if (!r.ok) { fout = `HTTP ${r.status}`; return; }
        planningGewijzigd();
        await fetchCallsheet(false);
      });
      if (!gelopen) throw new Error('de planning wordt nog opgeslagen — probeer het zo nog eens');
      if (fout) throw new Error(fout);
    }, { eigenKnop: true });
  };

  // Recalculate all planning times based on order and durations
  const recalculatePlanningTimes = (orderedPlanning = null) => metPlanningSlot(async () => {
    const itemsToUpdate = orderedPlanning || [...planning].sort((a, b) => (a.fields?.Tijd || 0) - (b.fields?.Tijd || 0));

    if (itemsToUpdate.length === 0) return;

    // 🔒 Vastgezette regels houden hun begintijd; wat erna komt sluit aan op het
    // einde van de vaste regel (Joris 18 sep: na een vaste lunch schuiven de
    // middagopnames mee). De cascade start bij de eerste NIET-vaste regel.
    const sloten = lockedRef.current;   // actueel, ook als deze functie uit een oude closure komt
    const firstFree = itemsToUpdate.find((item) => !sloten.has(item.id));
    let currentTime = firstFree?.fields?.Tijd ?? itemsToUpdate[0].fields?.Tijd ?? 28800;

    const updates = [];
    for (const item of itemsToUpdate) {
      const duration = (item.fields?.['Duur (minuten)'] || 15) * 60; // minuten → seconden
      if (sloten.has(item.id)) {
        currentTime = Math.max(currentTime, (item.fields?.Tijd || 0) + duration); // vast: tijd blijft, rest sluit erop aan
        continue;
      }
      const newTime = currentTime;
      currentTime = newTime + duration;
      if (newTime !== (item.fields?.Tijd || 0)) updates.push({ id: item.id, tijd: newTime });
    }

    if (updates.length === 0) { refreshData(); return; }
    // Oude tijden bewaren voor "Ongedaan maken" (herberekenen raakt alle regels).
    const oud = updates.map((u) => ({ id: u.id, tijd: itemsToUpdate.find((x) => x.id === u.id)?.fields?.Tijd || 0 }));
    // Undo-snapshot vóór de request (Codex r1): een wijziging tijdens het opslaan maakt undo ongeldig.
    planningGewijzigd();
    const versie = planningVersieRef.current;
    const slotenNa = [...lockedRef.current].sort().join(',');

    try {
      // Batch update all items
      const response = await authFetch(`${BACKEND_URL}/Api/callsheet-planning/batch-update-times`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update times');
      }
      wisOpslagFout();

      await fetchCallsheet(false);
      biedTijdenOngedaanAan(`${updates.length} planningstijd${updates.length === 1 ? '' : 'en'} herberekend`, updates, oud, versie, slotenNa);
    } catch (err) {
      console.error('Error recalculating times:', err);
      toonOpslagFout('Fout bij herberekenen tijden: ' + err.message);
    }
  });
  
  // Parkeer-check voor een locatie (PDOK + OSM + RDW via de backend).
  const checkParking = async (loc) => {
    const f = loc.fields || {};
    const address = f.Adres || [f.Straat, f.Postcode, f.Plaats].filter(Boolean).join(' ');
    if (!address) { setParking(prev => ({ ...prev, [loc.id]: { error: 'Geen adres ingevuld' } })); return; }
    setParkingLoading(prev => ({ ...prev, [loc.id]: true }));
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/parking/check?address=${encodeURIComponent(address)}`);
      const data = await res.json();
      setParking(prev => ({ ...prev, [loc.id]: res.ok ? data : { error: data.error || 'Check mislukt' } }));
    } catch (err) {
      setParking(prev => ({ ...prev, [loc.id]: { error: err.message } }));
    } finally {
      setParkingLoading(prev => ({ ...prev, [loc.id]: false }));
    }
  };

  // Zet het parkeeradvies in het 'Parkeren'-veld van de locatie (verschijnt op de PDF).
  const saveParkingNote = async (loc, text) => {
    try {
      await authFetch(`${BACKEND_URL}/Api/callsheet-locaties/${loc.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Parkeren: text }),
      });
      fetchCallsheet(false);
    } catch (err) { setError(err.message); }
  };

  // Delete location
  const deleteLocatie = async (locatieId) => {
    if (!(await bevestig({ titel: 'Locatie verwijderen?', tekst: 'Van deze callsheet.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      await authFetch(`${BACKEND_URL}/Api/callsheet-locaties/${locatieId}`, { method: 'DELETE' });
      fetchCallsheet(false);
    } catch (err) {
      setError(err.message);
    }
  };
  
  // Delete cast member
  const deleteCast = async (castId) => {
    if (!(await bevestig({ titel: 'Castlid verwijderen?', tekst: 'Van deze callsheet.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      await authFetch(`${BACKEND_URL}/Api/callsheet-cast/${castId}`, { method: 'DELETE' });
      fetchCallsheet(false);
    } catch (err) {
      setError(err.message);
    }
  };
  
  // Fetch callsheets for copying planning
  const fetchCopyPlanningOptions = useCallback(async () => {
    setLoadingCopyOptions(true);
    try {
      // Get current project ID from callsheet data
      const projectId = callsheetData?.callsheet?.fields?.['Project (from DraaidagenOutlook)']?.[0] || '';
      const response = await authFetch(
        `${BACKEND_URL}/Api/callsheets-with-planning?projectId=${projectId}&search=${encodeURIComponent(copyPlanningSearch)}`
      );
      if (response.ok) {
        const data = await response.json();
        // Filter out current callsheet
        setCopyPlanningOptions(data.filter(cs => cs.id !== id));
      }
    } catch (err) {
      console.error('Error fetching copy options:', err);
    } finally {
      setLoadingCopyOptions(false);
    }
  }, [callsheetData, copyPlanningSearch, id]);

  // Open copy planning dialog
  const handleOpenCopyDialog = () => {
    setCopyPlanningDialog(true);
    fetchCopyPlanningOptions();
  };

  // Copy planning from selected callsheet
  const handleCopyPlanning = async (sourceCallsheetId) => {
    const confirmMsg = clearExistingPlanning 
      ? 'Planning kopiëren? De bestaande planning wordt eerst verwijderd.' 
      : 'Planning kopiëren? De items worden toegevoegd aan de bestaande planning.';
    
    if (!(await bevestig({ titel: 'Planning kopiëren?', tekst: confirmMsg, knop: 'Kopiëren' }))) return;
    
    planningGewijzigd();
    setLoadingTemplate(true);
    setCopyPlanningDialog(false);
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/callsheet-planning/copy-from`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetCallsheetId: id,
          sourceCallsheetId,
          clearExisting: clearExistingPlanning,
          includeCrew: copyIncludeCrew,
          includeCast: copyIncludeCast
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Kopiëren mislukt');

      console.log(`Copied ${result.created} planning, ${result.crewCreated || 0} crew, ${result.castCreated || 0} cast`);
      fetchCallsheet(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingTemplate(false);
    }
  };

  // Load planning from template
  const handleLoadFromTemplate = async () => {
    if (!(await bevestig({ titel: 'Planning laden uit template?', tekst: 'Dit voegt standaard items toe aan de bestaande planning.', knop: 'Laden' }))) return;
    
    planningGewijzigd();
    setLoadingTemplate(true);
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/callsheet-planning/from-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ callsheetId: id })
      });
      
      const result = await response.json();
      
      if (!response.ok) throw new Error(result.error || 'Template laden mislukt');
      
      console.log(`Created ${result.created} planning items from template`);
      fetchCallsheet(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingTemplate(false);
    }
  };
  
  // Haalt de gegenereerde PDF op als blob (met Bearer-token) → { url, blob }.
  const fetchPdfBlob = async () => {
    const resp = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}/download-pdf`);
    if (!resp.ok) throw new Error('PDF ophalen mislukt');
    const blob = await resp.blob();
    // Naam uit de server-header (filename* gaat voor), anders dezelfde regel lokaal. Een blob-URL
    // heeft zelf geen naam: wie hem zonder download-naam opslaat krijgt een uuid (Joris 29 sep).
    const filename = bestandsnaamUitHeader(resp.headers?.get?.('Content-Disposition')) || buildPdfFilename(callsheetData?.callsheet?.fields);
    return { url: URL.createObjectURL(blob), blob, filename };
  };

  const closePdfPreview = () => {
    setPdfPreview(prev => {
      if (prev.url) URL.revokeObjectURL(prev.url);
      return { open: false, url: '', filename: '' };
    });
  };

  // Toon de PDF in een in-app preview-dialog (i.p.v. een nieuw tabblad dat in sommige
  // browsers direct downloadt i.p.v. previewt).
  const openPdfPreview = async () => {
    try {
      const { url, filename } = await fetchPdfBlob();
      setPdfPreview({ open: true, url, filename });
    } catch (e) { setError(e.message); }
  };

  // "Opslaan als…": native Opslaan-als-dialoog via de File System Access API waar
  // beschikbaar (Chrome/Edge); anders fallback op een gewone download met nette naam.
  const savePdfAs = async () => {
    try {
      const { url, blob, filename } = await fetchPdfBlob();
      if (window.showSaveFilePicker) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{ description: 'PDF', accept: { 'application/pdf': ['.pdf'] } }],
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          URL.revokeObjectURL(url);
          return;
        } catch (pickErr) {
          if (pickErr?.name === 'AbortError') { URL.revokeObjectURL(url); return; } // gebruiker annuleerde
          // anders: val terug op gewone download hieronder
        }
      }
      // Altijd via <a download> mét naam, in de DOM (Firefox/Safari negeren anders de naam).
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { setError(e.message); }
  };

  // Generate PDF
  const handleGeneratePdf = async () => {
    // Vooraf-check: waarschuw voor ontbrekende velden vóór het genereren.
    const f = callsheetData?.callsheet?.fields || {};
    const crewList = callsheetData?.crew || [];
    const planningList = callsheetData?.planning || [];
    const locatieList = callsheetData?.locaties || [];
    const phoneOf = (c) => c.fields?.['Telefoon (handmatig)'] || c.fields?.['Telefoonnummer (from Personen)']?.[0] || c.fields?.Telefoonnummer;
    const missing = [];
    if (!(f['FullFrame Settelefoonnummer']?.[0] || f['Naam (from Contactpersoon Settelefoon)']?.[0])) missing.push('Settelefoon');
    if (!(f['Contactpersoon (handmatig)'] || f.Contactpersoon)) missing.push('Contactpersoon klant');
    if (!(f['Contactgegevens klant telefoon (Handmatig)'] || f['Telefoonnummer klant'])) missing.push('Telefoonnummer klant');
    if (locatieList.length === 0) missing.push('Geen locatie toegevoegd');
    const crewNoPhone = crewList.filter(c => !phoneOf(c)).length;
    if (crewNoPhone) missing.push(`${crewNoPhone} crewlid(en) zonder telefoonnummer`);
    const crewNoCall = crewList.filter(c => !c.fields?.Calltijd).length;
    if (crewNoCall) missing.push(`${crewNoCall} crewlid(en) zonder calltijd`);
    const planNoLoc = planningList.filter(p => !p.fields?.Locatie).length;
    if (planNoLoc) missing.push(`${planNoLoc} planning-item(s) zonder locatie`);
    // Parkeren: attendeer de PL als er locaties zijn zonder parkeerinfo.
    const locNoParking = locatieList.filter(l => !(l.fields?.Parkeren || '').trim()).length;
    if (locNoParking) missing.push(`${locNoParking} locatie(s) zonder parkeerinfo — check parkeren (knop bij de locatie)`);
    if (missing.length && !skipMissingWarnRef.current) {
      const proceed = await bevestig({
        titel: 'Mogelijk ontbreekt nog iets',
        tekst: `• ${missing.join('\n• ')}\n\nToch de PDF genereren? Je krijgt deze melding daarna niet meer deze sessie.`,
        knop: 'Toch genereren',
      });
      if (!proceed) return;
      // Eenmaal genegeerd → niet meer zeuren deze sessie.
      skipMissingWarnRef.current = true;
    }

    setGenerating(true);
    setPdfStatus('Bezig met genereren...');
    setError(null);

    try {
      const response = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}/generate-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ includeWeather: true })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'PDF generatie mislukt');
      }
      
      if (result.pdfBase64) {
        setPdfStatus('PDF is gereed!');
        // PDF meteen in-app previewen (geen nieuw tabblad dat soms downloadt).
        // Downloaden/Opslaan-als kan vanuit de preview of via de losse knoppen.
        try {
          const { url, filename } = await fetchPdfBlob();
          setPdfPreview({ open: true, url, filename });
        } catch (dlErr) { console.error('PDF preview error:', dlErr); }
      } else if (result.html) {
        setPdfStatus('HTML gegenereerd');
        // Open print preview
        const printWindow = window.open('', '_blank');
        printWindow.document.write(result.html);
        printWindow.document.close();
        printWindow.print();
      }
      
      await fetchCallsheet(false);
    } catch (err) {
      setError(err.message);
      setPdfStatus(`Fout: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };
  
  // Delete callsheet
  const handleDelete = async () => {
    if (!(await bevestig({ titel: 'Callsheet verwijderen?', tekst: 'De callsheet met crew, cast, locaties en planning wordt uit Airtable verwijderd.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Verwijderen mislukt');
      
      navigate(-1);
    } catch (err) {
      setError(err.message);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error && !callsheetData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/callsheets')}>Terug</Button>
      </Box>
    );
  }
  
  const { callsheet, crew, cast, planning, locaties: locatiesRaw, draaidag } = callsheetData || {};
  const fields = callsheet?.fields || {};
  // Draaidag als pure datum (YYYY-MM-DD) voor de reistijd in "Waar".
  const draaidagIso = String(fields['Datum (handmatig)'] || fields.Datum || '').slice(0, 10);
  // 'Projectleider (from Project)' is een linked-record lookup → geeft record-ID's terug.
  // Resolve naar naam via de personen-lijst (looksLikeAirtableId-patroon); val terug op niets
  // i.p.v. een rauwe recXXX te tonen.
  const projectleiderRaw = draaidag?.[0]?.fields?.['Projectleider (from Project)'];
  const projectleider = (() => {
    const arr = Array.isArray(projectleiderRaw) ? projectleiderRaw : (projectleiderRaw ? [projectleiderRaw] : []);
    const names = arr.map(v => {
      if (typeof v === 'string' && /^rec[A-Za-z0-9]{14}$/.test(v)) {
        const found = personen.find(p => p.id === v);
        return found?.fields?.['Volledige naam'] || found?.fields?.Naam || null;
      }
      return v;
    }).filter(Boolean);
    return names.length ? names.join(', ') : null;
  })();

  // Projectnummer (+ -naam) uit de gekoppelde draaidag/project — tonen in de header.
  const projectnummer = (() => {
    const raw = draaidag?.[0]?.fields?.['Projectnummer (from Project)'];
    const v = Array.isArray(raw) ? raw[0] : raw;
    return v != null && v !== '' ? String(v) : null;
  })();
  const projectnaam = (() => {
    const raw = draaidag?.[0]?.fields?.['Projectnr_en_naam (from Project)']
      || draaidag?.[0]?.fields?.['Naam (from Project)'];
    const v = Array.isArray(raw) ? raw[0] : raw;
    return v != null && v !== '' ? String(v) : null;
  })();

  // Projectstrook (28 sep): project zoals de engine het voor de AI bepaalt
  // (callsheet_briefing.ai_briefing) — recId uit 'Project (from DraaidagenOutlook)',
  // nummer uit het callsheet (of de draaidag / 'Projectnr_en_naam'), klant vóór ' - '.
  const strookProjectId = (fields['Project (from DraaidagenOutlook)'] || [])[0] || '';
  const strookProjectnummer = (() => {
    const eigen = String(fields.Projectnummer || '').trim();
    if (eigen) return eigen;
    if (projectnummer) return projectnummer;
    const nrNaam = fields['Projectnr_en_naam (from Project) (from DraaidagenOutlook)'];
    const m = String(Array.isArray(nrNaam) ? nrNaam[0] || '' : nrNaam || '').match(/^\s*(\d{4})/);
    return m ? m[1] : '';
  })();
  const strookKlant = (() => {
    const raw = fields.Klant || fields['Opdrachtgever (Naam) (from Draaidag)'];
    const v = Array.isArray(raw) ? raw.filter(Boolean).join(', ') : String(raw || '');
    return v.split(' - ')[0].trim();
  })();
  const strookProjectnaam = projectnaam || (() => {
    const raw = fields['Projectnr_en_naam (from Project) (from DraaidagenOutlook)'];
    return Array.isArray(raw) ? raw[0] || '' : raw || '';
  })();

  // Sort locaties so the first location used in planning (by time) appears first
  // This reduces errors by making the first shoot location prominent
  const sortedPlanningForLocaties = (planning || []).slice().sort((a, b) => (a.fields?.Tijd || 0) - (b.fields?.Tijd || 0));
  const locationFirstUsage = new Map();
  sortedPlanningForLocaties.forEach(p => {
    const locName = (p.fields?.Locatie || '').toLowerCase().trim();
    if (locName && !locationFirstUsage.has(locName)) {
      locationFirstUsage.set(locName, p.fields?.Tijd || 0);
    }
  });
  // Volgorde = "in welke volgorde rij je ernaartoe" (Joris, 7 sep): handmatig gezette
  // Volgorde wint, daarna het eerste planning-item dat de locatie noemt.
  const locaties = (locatiesRaw || []).slice().sort((a, b) => {
    const va = a.fields?.Volgorde ?? Infinity;
    const vb = b.fields?.Volgorde ?? Infinity;
    if (va !== vb) return va - vb;
    const nameA = (a.fields?.['Naam locatie'] || '').toLowerCase().trim();
    const nameB = (b.fields?.['Naam locatie'] || '').toLowerCase().trim();
    const timeA = locationFirstUsage.get(nameA) ?? Infinity;
    const timeB = locationFirstUsage.get(nameB) ?? Infinity;
    return timeA - timeB;
  });
  const verplaatsLocatie = (index, richting) => {
    const nieuw = [...locaties];
    const doel = index + richting;
    if (doel < 0 || doel >= nieuw.length) return;
    [nieuw[index], nieuw[doel]] = [nieuw[doel], nieuw[index]];
    persistRowOrder(nieuw, 'locaties', 'callsheet-locaties');
  };
  
  // Klik → veld (gedeeld met de klaar-regel, die een veld kan openen).
  const editCtx = { editingField, editValue, setEditValue, setEditingField };

  // Eén PATCH voor een of meer callsheetvelden, optimistisch.
  const patchVelden = async (patch, { alleenVerzenden } = {}) => {
    setCallsheetData(prev => prev ? {
      ...prev,
      callsheet: { ...prev.callsheet, fields: { ...prev.callsheet.fields, ...patch } },
    } : prev);
    // Optimistisch mag meer velden raken (formules) dan er naar Airtable gaat.
    const teVerzenden = alleenVerzenden ? Object.fromEntries(alleenVerzenden.map((k) => [k, patch[k]])) : patch;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(teVerzenden),
      });
      if (!r.ok) throw new Error(`Opslaan mislukt (HTTP ${r.status})`);
      if (opslagFoutRef.current) { opslagFoutRef.current = false; setError(null); }
      return true;
    } catch (err) {
      opslagFoutRef.current = true;
      setError(err.message);
      await fetchCallsheet(false);   // herstel vanuit de server; de fout blijft staan
      return false;
    }
  };
  // Contact/telefoon klant: de formulevelden ('Contactpersoon', 'Telefoonnummer klant') vallen terug op
  // het project als het handmatige veld leeg is. Die terugval kent alleen Airtable, dus na opslaan stil
  // herladen; tot dan tonen we een leeggemaakt veld niet als de oude waarde (Codex r2).
  const bewaarContact = async (patch) => {
    const formules = {};
    Object.values(CONTACT_VELDEN).forEach(({ handmatig, formule }) => {
      if (handmatig in patch) formules[formule] = patch[handmatig] || undefined;
    });
    const gelukt = await patchVelden({ ...patch, ...formules }, { alleenVerzenden: Object.keys(patch) });
    if (gelukt) fetchCallsheet(false);   // bij een fout heeft patchVelden al herladen
    return gelukt;
  };
  const kiesSettelefoon = async (p) => {
    const naam = p.fields?.['Volledige naam'] || p.fields?.Naam || '';
    const telRaw = p.fields?.Telefoonnummer;
    const tel = Array.isArray(telRaw) ? telRaw[0] : (telRaw || '');
    setEditingField(null);
    // Optimistisch bijwerken (incl. de opgezochte naam/nummer) → geen herlaad.
    setCallsheetData(prev => prev ? {
      ...prev,
      callsheet: { ...prev.callsheet, fields: { ...prev.callsheet.fields,
        'Contactpersoon Settelefoon': [p.id],
        'Naam (from Contactpersoon Settelefoon)': naam ? [naam] : [],
        'FullFrame Settelefoonnummer': tel ? [tel] : [],
      } },
    } : prev);
    try {
      await authFetch(`${BACKEND_URL}/Api/callsheets/${id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 'Contactpersoon Settelefoon': [p.id] }),
      });
      fetchCallsheet(false);   // stille reconcile (geen laad-indicator)
    } catch (err) { setError(err.message); fetchCallsheet(false); }
  };

  const heeftPdf = !!fields['PDF-bestand']?.[0]?.url;
  const { opmerkingen: opmerkingenTekst, oplevering } = splitsOplevering(fields['Algemene opmerkingen']);
  const ontbrekend = bepaalOntbrekend({ fields, extra, locaties, crew });
  const isOpen = (key) => ontbrekend.some((p) => p.key === key);
  const settel = settelefoonVan(fields);
  const contactNaam = contactStatus(fields, 'naam');
  const contactTel = contactStatus(fields, 'telefoon');
  const grijsTekst = (t) => <Box component="span" sx={{ color: GRIJS }}>{t}</Box>;
  const planningGesorteerd = [...(planning || [])].filter((p) => p.fields?.Tijd != null).sort((a, b) => (a.fields.Tijd || 0) - (b.fields.Tijd || 0));
  const tijdenUitPlanning = () => {
    if (!planningGesorteerd.length) return;
    const begin = planningGesorteerd[0].fields.Tijd || 0;
    const eind = Math.max(...planningGesorteerd.map((p) => (p.fields.Tijd || 0) + ((p.fields['Duur (minuten)'] || 0) * 60)));
    bewaarExtra({ begintijd: formatDuration(begin), eindtijd: formatDuration(eind), tijdenBron: 'planning' });
  };
  const naarBeeld = (selector) => {
    // Na de render (bewerkmodus of dialoog) pas scrollen, anders staat het element er nog niet.
    setTimeout(() => {
      const el = document.querySelector(selector);
      el?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }, 0);
  };
  // Klaar-regel: spring naar het veld en open het (of de dialoog bij locatie/crew).
  const VELD_VAN = {
    begintijd: ['begintijd', extra.begintijd],
    eindtijd: ['eindtijd', extra.eindtijd],
    contact: ['contact', contactNaam.handmatig],
    telefoon: ['Contactgegevens klant telefoon (Handmatig)', contactTel.handmatig],
    settelefoon: ['settelefoon', ''],
  };
  const spring = (key) => {
    if (key === 'locatie') { naarBeeld('#cs-rij-locatie'); setLocatieDialog({ open: true, data: null }); return; }
    if (key === 'crew') { naarBeeld('#cs-rij-crew'); setCrewDialog({ open: true, data: null }); return; }
    const [veld, waarde] = VELD_VAN[key] || [];
    if (!veld) return;
    setEditValue(waarde || '');
    setEditingField(veld);
    naarBeeld(`[data-veld="${veld}"]`);
  };
  const crewNaam = (c) => c.fields?.Persoon || c.fields?.['Volledige naam (from Personen)']?.[0] || '-';
  const castNaam = (c) => c.fields?.Naam || c.fields?.['Naam (from Cast)']?.[0] || c.fields?.['Cast (handmatig)'] || '-';
  const projectRegel = [
    strookProjectnummer ? `#${strookProjectnummer}` : '',
    kaleProjectnaam(strookProjectnaam, strookProjectnummer) || strookKlant,
  ].filter(Boolean).join(' ');
  const rijKnopSx = { p: 0.25, color: '#94a3b8', '&:hover': { color: '#334155' } };
  // Grote, scanbare tijden in het kernblok (begin/eind van de dag).
  const tijdGrootSx = { ...tijdSx(SECTIE_ACCENT.waar.kleur), fontSize: 22, lineHeight: 1.25 };
  // "call 08:00": het woord rustig, de tijd vet en met vaste cijferbreedte.
  const callTijd = (sec) => (sec != null ? (
    <><Box component="span" sx={{ color: GRIJS, fontSize: 13 }}>call </Box><Box component="span" sx={tijdSx(INKT)}>{formatDuration(sec)}</Box></>
  ) : '');

  return (
    <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 8 }}>
      <Button size="small" startIcon={<BackIcon />} onClick={() => navigate('/callsheets')}
        sx={{ ...tekstKnopSx, color: GRIJS, mb: 1, ml: -1 }}>
        Callsheets
      </Button>

      {/* ── Kop: titel, datum, project, klaar-regel en de acties (Joris 28 sep) ── */}
      <Box component="header" data-testid="callsheet-kop" sx={{ mb: 2.5 }}>
        {/* Titel over de volle breedte, hoogstens twee regels (volledige titel in de tooltip);
            de acties staan rechts op de regel eronder (Joris 29 sep: lange TIVO-titel brak over drie regels). */}
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: GRIJS }}>Callsheet</Typography>
        <KlikVeld veld="Titel" label="Titel" waarde={fields.Titel} groot titelRegels={2} ctx={editCtx} leeg="Naamloos"
          onBewaar={(v) => updateField('Titel', v)} />

        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2, rowGap: 1, flexWrap: 'wrap', mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', columnGap: 1, rowGap: 0.25, flexWrap: 'wrap', flex: '1 1 380px', minWidth: 0 }}>
            <KlikVeld veld="Datum (handmatig)" label="Datum" soort="datum" ctx={editCtx}
              waarde={fields['Datum (handmatig)'] || (fields.Datum ? String(fields.Datum).slice(0, 10) : '')}
              weergave={formatDate(fields['Datum (handmatig)'] || fields.Datum)} leeg="datum kiezen"
              waardeSx={{ fontSize: 17, fontWeight: 700, color: INKT }}
              onBewaar={(v) => updateField('Datum (handmatig)', v)} />
            {projectRegel && <Typography component="span" variant="body2" sx={{ color: '#334155' }}>· {projectRegel}</Typography>}
            <Typography component="span" variant="body2" sx={{ color: GRIJS }}>·</Typography>
            <KlaarRegel ontbrekend={ontbrekend} onSpring={spring} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', ml: { md: 'auto' } }} data-testid="kop-acties">
            <Tooltip title="Leest de notities, meetings, mails en het dossier van dit project en stelt opmerkingen, productievelden en een briefing voor de crew voor. Jij kiest wat je overneemt.">
              <span>
                <Button variant="contained" disableElevation onClick={() => vraagAiBriefing()} disabled={aiBriefingBusy}
                  startIcon={ai.starten ? <CircularProgress size={14} color="inherit" /> : null}
                  sx={{ textTransform: 'none', fontWeight: 700 }}>
                  {aiBriefingBusy ? 'AI bereidt voor…' : 'AI voorbereiden'}
                </Button>
              </span>
            </Tooltip>
            <Button variant="outlined" onClick={(e) => setPdfMenu(e.currentTarget)} disabled={generating}
              startIcon={generating ? <CircularProgress size={14} color="inherit" /> : null}
              aria-haspopup="menu" sx={{ textTransform: 'none', fontWeight: 600 }}>
              {generating ? 'PDF maken…' : 'PDF'}
            </Button>
            <WordExportKnop callsheetId={id} fields={fields} metIcoon={false} sx={{ textTransform: 'none', fontWeight: 600 }} />
            <Tooltip title={(crew || []).length ? 'Laat de crew weten dat de callsheet is bijgewerkt (melding in intern, crew-app en/of e-mail)' : 'Voeg eerst crew toe'}>
              <span>
                <Button variant="outlined" disabled={!(crew || []).length}
                  onClick={() => setInformeer({ bericht: '', webapp: true, crewApp: true, mail: false, bezig: false })}
                  sx={{ textTransform: 'none', fontWeight: 600 }}>
                  Crew informeren
                </Button>
              </span>
            </Tooltip>
            <ProjectOpenenKnop projectId={strookProjectId} />
            <IconButton size="small" aria-label="Meer acties" onClick={(e) => setMeerMenu(e.currentTarget)} sx={{ color: GRIJS }}>
              <MoreIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ mt: 0.75 }}>
          <ProjectStrook inline callsheetId={id} projectId={strookProjectId} projectnummer={strookProjectnummer}
            projectnaam={strookProjectnaam} klant={strookKlant}
            onOpnieuwAi={() => vraagAiBriefing()} aiBezig={aiBriefingBusy} />
        </Box>
        {(strookProjectId || strookProjectnummer) && (
          <KlantVereistenBlok variant="detail" projectId={strookProjectId} nummer={strookProjectnummer} sx={{ mt: 1.5, mb: 0 }} />
        )}
        {ai.job && !aiOpnieuwBezig && (
          <Box sx={{ mt: 0.5 }}>
            <AiVoortgang job={ai.job} ingevuld={aiIngevuld} voorstelAantal={aiVoorstelLijst.length} punten={aiPunten}
              fouten={aiInvulFouten} onStop={ai.stop} onVoorstel={bekijkAiVoorstel} onHerstel={herstelAiBlok}
              onSluit={() => { ai.sluit(); setAiIngevuld([]); setAiVoorstelBlokken([]); setAiInvulFouten([]); }} />
          </Box>
        )}

        {generating && (
          <Box sx={{ mt: 1.5 }}>
            <LinearProgress />
            <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: GRIJS }}>PDF maken kan even duren (tot 30 sec)…</Typography>
          </Box>
        )}
        {fields.StatusPDFWarning && fields.StatusPDFWarning.includes('Fout') && (
          <Alert severity="error" sx={{ mt: 1.5 }}>{fields.StatusPDFWarning}</Alert>
        )}
        {error && <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => { opslagFoutRef.current = false; setError(null); }}>{error}</Alert>}
      </Box>

      <Menu anchorEl={pdfMenu} open={!!pdfMenu} onClose={() => setPdfMenu(null)}>
        {pdfStatus && <MenuItem disabled sx={{ fontSize: 13 }}>{pdfStatus}</MenuItem>}
        {fields.StatusPDFWarning && !fields.StatusPDFWarning.includes('Fout') && <MenuItem disabled sx={{ fontSize: 13, whiteSpace: 'normal', maxWidth: 360 }}>{fields.StatusPDFWarning}</MenuItem>}
        <MenuItem onClick={() => { setPdfMenu(null); handleGeneratePdf(); }}>{heeftPdf ? 'Nieuwe PDF maken (verversen)' : 'PDF maken'}</MenuItem>
        {heeftPdf && <MenuItem onClick={() => { setPdfMenu(null); openPdfPreview(); }}>Bekijken</MenuItem>}
        {heeftPdf && <MenuItem onClick={() => { setPdfMenu(null); savePdfAs(); }}>Opslaan als…</MenuItem>}
      </Menu>
      <Menu anchorEl={meerMenu} open={!!meerMenu} onClose={() => setMeerMenu(null)}>
        <MenuItem onClick={() => { setMeerMenu(null); setDuplicateTarget(callsheet); }}>Dupliceren</MenuItem>
        <MenuItem onClick={() => { setMeerMenu(null); fetchCallsheet(false); }}>Vernieuwen</MenuItem>
        {briefingTekst && <MenuItem onClick={() => { setMeerMenu(null); navigate(`/callsheets/${id}/briefing`); }}>Briefing openen</MenuItem>}
        <MenuItem onClick={() => { setMeerMenu(null); handleDelete(); }} sx={{ color: 'error.main' }}>Verwijderen</MenuItem>
      </Menu>

      <Dialog open={Boolean(informeer) && !verzendCheck} onClose={() => !informeer?.bezig && setInformeer(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Crew informeren</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            Naar {(crew || []).length} crewleden: {(crew || []).map((c) => c.fields?.['Volledige naam (from Personen)']?.[0] || c.fields?.Persoon || '?').filter(Boolean).join(', ')}
          </Typography>
          <TextField fullWidth multiline minRows={2} size="small" label="Bericht (optioneel)"
            placeholder="De callsheet is bijgewerkt — bekijk de laatste versie."
            value={informeer?.bericht || ''} onChange={(e) => setInformeer((x) => ({ ...x, bericht: e.target.value }))} sx={{ mb: 1.5 }} />
          <FormControlLabel control={<Checkbox checked={Boolean(informeer?.webapp)} onChange={(e) => setInformeer((x) => ({ ...x, webapp: e.target.checked }))} />}
            label="Melding in intern (belletje)" />
          <FormControlLabel control={<Checkbox checked={Boolean(informeer?.crewApp)} onChange={(e) => setInformeer((x) => ({ ...x, crewApp: e.target.checked }))} />}
            label="Update in de FF Crew-app" />
          <FormControlLabel control={<Checkbox checked={Boolean(informeer?.mail)} onChange={(e) => setInformeer((x) => ({ ...x, mail: e.target.checked }))} />}
            label="E-mail naar de crew" />
          <BijlagenInDialoog overzicht={bijlagenOverzicht} mail={Boolean(informeer?.mail)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInformeer(null)} disabled={informeer?.bezig} sx={{ textTransform: 'none' }}>Annuleren</Button>
          <Button variant="contained" disableElevation onClick={() => setVerzendCheck(true)} disabled={informeer?.bezig || !(informeer?.webapp || informeer?.crewApp || informeer?.mail) || Boolean(informeer?.mail && bijlagenProbleem(bijlagenOverzicht))} sx={{ textTransform: 'none' }}>
            {informeer?.bezig ? 'Bezig…' : 'Versturen'}
          </Button>
        </DialogActions>
      </Dialog>
      <CallsheetVerzendCheck
        open={Boolean(informeer) && verzendCheck && !informeer?.bezig}
        feiten={feitenUitCallsheet({ fields, draaidag, extra, locaties, crew, personen })}
        kanalen={informeer || {}}
        aantalBijlagen={aantalBijlagen(bijlagenOverzicht)}
        laadOntvangers={laadCrewOntvangers}
        onTerug={() => setVerzendCheck(false)}
        onVerstuur={(bevestigd) => { setVerzendCheck(false); verstuurInformeer(bevestigd); }} />
      <DuplicateCallsheetDialog callsheet={duplicateTarget} onClose={() => setDuplicateTarget(null)} />
      <Dialog open={!!aiBriefing} onClose={() => setAiBriefing(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          ✨ Voorstel uit het project
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1.5, cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setAiBronnenOpen((v) => !v)}>
            {(aiBriefing?.bronnen || []).length} bronnen gelezen {aiBronnenOpen ? '▾' : '▸'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {aiBronnenOpen && (
            <Box sx={{ mb: 2, p: 1.25, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0', maxHeight: 160, overflow: 'auto' }}>
              {(aiBriefing?.bronnen || []).map((b) => (
                <Typography key={b.ref} variant="caption" sx={{ display: 'block', color: '#475569' }}>
                  · {b.url ? <a href={b.url} target="_blank" rel="noreferrer" style={{ color: '#4338ca' }}>{b.label}</a> : b.label} <span style={{ color: '#94a3b8' }}>[{b.ref}]</span>
                </Typography>
              ))}
            </Box>
          )}

          {(aiBriefing?.conflicten || []).length > 0 && (
            <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, border: '1px solid #fcd34d', bgcolor: '#fffbeb' }}>
              <Typography fontWeight={800} sx={{ mb: 0.5 }}>⚠️ Bronnen spreken elkaar tegen — jij kiest</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Standaard staat de nieuwste bron aan. Kies anders en klik onderaan op “Opnieuw met mijn keuzes”.</Typography>
              {aiBriefing.conflicten.map((c, i) => (
                <Box key={i} sx={{ mb: 1.25 }}>
                  <Typography variant="body2" fontWeight={700}>{c.onderwerp}</Typography>
                  {c.versies.map((v, j) => (
                    <FormControlLabel key={j} sx={{ display: 'flex', alignItems: 'flex-start', ml: 0, mr: 0, mb: 0.25 }}
                      control={<Checkbox size="small" checked={aiConflictKeuze[i] === v.waarde} onChange={() => setAiConflictKeuze((p) => ({ ...p, [i]: v.waarde }))} sx={{ py: 0.25 }} />}
                      label={<Typography variant="body2"><b>{v.waarde}</b> <Typography component="span" variant="caption" color="text.secondary">— {v.bron}{v.datum && v.datum !== '?' ? `, ${v.datum}` : ''}</Typography></Typography>} />
                  ))}
                  {c.reden && <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>AI koos: {c.gekozen} ({c.reden})</Typography>}
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5 }}>
            {[['Callsheet (productie)', ['algemene_opmerkingen', 'props', 'styling', 'visagie', 'lunch', 'reminder', 'kledingvoorschriften']], ['Briefing crew', ['briefing_crew']]].map(([label, keys], idx) => {
              const n = keys.filter((k) => aiKeuze[k]).length + (idx === 0 ? Object.keys(aiExtraKeuze).length : 0);
              return (
                <Button key={label} size="small" variant={aiTab === idx ? 'contained' : 'outlined'} disableElevation onClick={() => setAiTab(idx)}
                  sx={{ textTransform: 'none', fontWeight: 700, ...(aiTab === idx ? { bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } } : { borderColor: '#c4b5fd', color: '#5b21b6' }) }}>
                  {label}{n ? ` · ${n}` : ''}
                </Button>
              );
            })}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {aiTab === 0 ? 'Productieniveau: tijden, locatie, contact, wie wat meeneemt. Staat op het callsheet dat ook de klant ziet.' : 'Achtergrond voor cameraman en regisseur: verhaal, shots, referenties met links. Eigen document, niet voor de klant.'}
          </Typography>

          {Object.entries(aiKeuze).filter(([k]) => (aiTab === 1) === (k === 'briefing_crew')).map(([k, v]) => (
            <Box key={k} sx={{ mb: 1.5, p: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: v.aan ? '#c4b5fd' : '#e2e8f0', bgcolor: v.aan ? '#faf5ff' : '#fff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Checkbox size="small" checked={!!v.aan} onChange={(e) => setAiKeuze((p) => ({ ...p, [k]: { ...p[k], aan: e.target.checked } }))} sx={{ p: 0.5 }} />
                <Typography fontWeight={700} variant="body2">{AI_LABELS[k] || k}</Typography>
                {v.bestaand && <Chip size="small" label="vervangt bestaande tekst" sx={{ height: 18, fontSize: 10.5, bgcolor: '#fef3c7', color: '#92400e' }} />}
              </Box>
              {k === 'briefing_crew' && !aiBriefingBewerk ? (
                <Box>
                  <Box sx={{ maxHeight: 420, overflow: 'auto', px: 1, py: 0.5, bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1, '& h1, & h2, & h3': { fontSize: '0.92rem', fontWeight: 800, mt: 1.5, mb: 0.5 }, '& ul': { pl: 2.5, my: 0.5 }, '& li': { mb: 0.3, lineHeight: 1.5, fontSize: '0.86rem' }, '& p': { lineHeight: 1.55, fontSize: '0.86rem', my: 0.5 }, '& a': { color: '#4338ca', fontSize: '0.78rem', textDecoration: 'none', bgcolor: '#eef2ff', borderRadius: 0.75, px: 0.5 } }}>
                    <ReactMarkdown components={mdLinkComponents}>{bronRefsNaarLinks(v.tekst, aiBriefing?.bronnen || [])}</ReactMarkdown>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Bronnen staan als klikbare labels; de crew en de PDF zien ze niet.</Typography>
                    <Button size="small" onClick={() => setAiBriefingBewerk(true)} sx={{ textTransform: 'none' }}>Tekst bewerken</Button>
                  </Box>
                </Box>
              ) : (
                <TextField fullWidth multiline minRows={k === 'briefing_crew' ? 12 : 2} maxRows={k === 'briefing_crew' ? 30 : 10} size="small" value={v.tekst}
                  onChange={(e) => setAiKeuze((p) => ({ ...p, [k]: { ...p[k], tekst: e.target.value } }))} inputProps={{ dir: 'ltr', style: { textAlign: 'left', fontSize: 13.5, lineHeight: 1.5 } }} />
              )}
              {k === 'briefing_crew' && aiBriefingBewerk && <Button size="small" onClick={() => setAiBriefingBewerk(false)} sx={{ textTransform: 'none', mt: 0.5 }}>Opgemaakt tonen</Button>}
            </Box>
          ))}

          {aiTab === 0 && Object.keys(aiExtraKeuze).length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>Ook uit de bronnen gehaald</Typography>
              {[
                aiExtraKeuze.locatie && ['locatie', '📍 Locatie', [aiExtraKeuze.locatie.naam, aiExtraKeuze.locatie.straat, [aiExtraKeuze.locatie.postcode, aiExtraKeuze.locatie.plaats].filter(Boolean).join(' '), aiExtraKeuze.locatie.opmerkingen].filter(Boolean).join(' · '), (callsheetData?.locaties || []).length ? 'komt als extra locatie' : null],
                aiExtraKeuze.settelefoon && ['settelefoon', '📞 Settelefoon', aiExtraKeuze.settelefoon.naam, aiExtraKeuze.settelefoon.persoon ? `koppelt ${aiExtraKeuze.settelefoon.persoon.fields?.['Volledige naam'] || ''}` : 'niet gevonden in Personen — kies hieronder handmatig'],
                aiExtraKeuze.contactKlant && ['contactKlant', '👤 Contactpersoon klant', [aiExtraKeuze.contactKlant.naam, aiExtraKeuze.contactKlant.telefoon].filter(Boolean).join(' · '), null],
                aiExtraKeuze.planning && ['planning', '🕐 Planning', aiExtraKeuze.planning.rows.map((r) => `${r.tijd} ${r.omschrijving}`).join(' · '), null],
              ].filter(Boolean).map(([k, titel, tekst, hint]) => (
                <Box key={k} sx={{ mb: 1, p: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: aiExtraKeuze[k].aan ? '#c4b5fd' : '#e2e8f0', bgcolor: aiExtraKeuze[k].aan ? '#faf5ff' : '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Checkbox size="small" checked={!!aiExtraKeuze[k].aan} disabled={k === 'settelefoon' && !aiExtraKeuze.settelefoon.persoon}
                      onChange={(e) => setAiExtraKeuze((p) => ({ ...p, [k]: { ...p[k], aan: e.target.checked } }))} sx={{ p: 0.5 }} />
                    <Typography fontWeight={700} variant="body2">{titel}</Typography>
                    {hint && <Chip size="small" label={hint} sx={{ height: 18, fontSize: 10.5, bgcolor: '#f1f5f9', color: '#475569' }} />}
                  </Box>
                  <Typography variant="body2" sx={{ ml: 4.5, color: '#334155' }}>{tekst}</Typography>
                  {k === 'planning' && (callsheetData?.planning || []).length > 0 && (
                    <FormControlLabel sx={{ ml: 3.5 }} control={<Checkbox size="small" checked={!!aiExtraKeuze.planning.vervang} onChange={(e) => setAiExtraKeuze((p) => ({ ...p, planning: { ...p.planning, vervang: e.target.checked } }))} />}
                      label={<Typography variant="caption">Bestaande planning ({(callsheetData?.planning || []).length} regels) vervangen</Typography>} />
                  )}
                </Box>
              ))}
            </Box>
          )}

          {(aiBriefing?.vragen || []).length > 0 && aiTab === 0 && (
            <Alert severity="info" sx={{ mt: 1, mb: 1.5 }}>
              <b>Nog checken bij de klant</b>
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>{aiBriefing.vragen.map((q, i) => <li key={i}>{q}</li>)}</ul>
            </Alert>
          )}

          <Box sx={{ mt: 2, p: 1.5, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <Typography fontWeight={800} variant="body2" sx={{ mb: 1 }}>Bijsturen</Typography>
            <TextField fullWidth multiline minRows={2} size="small" label="Aanwijzingen voor de AI" placeholder="Bijv. “Jef stopt om 15:15, planning in callsheet is oud” of “Gert komt niet, laat weg”." value={aiAanwijzingen} onChange={(e) => setAiAanwijzingen(e.target.value)} sx={{ mb: 1 }} inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }} />
            <TextField fullWidth multiline minRows={2} maxRows={10} size="small" label="Extra tekst als bron (mail, WhatsApp, notulen)" placeholder="Plak hier bijvoorbeeld een doorgestuurde mail die nog niet in het project staat." value={aiExtraBron} onChange={(e) => setAiExtraBron(e.target.value)} inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button size="small" variant="outlined" disabled={aiBriefingBusy} onClick={() => vraagAiBriefing({ opnieuw: true })}
                startIcon={aiOpnieuwBezig ? <CircularProgress size={13} /> : null} sx={{ textTransform: 'none', fontWeight: 700 }}>
                {aiOpnieuwBezig ? 'Bezig…' : 'Opnieuw met mijn keuzes en aanwijzingen'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiBriefing(null)} sx={{ textTransform: 'none' }}>Annuleren</Button>
          {aiOpnieuwBezig && <Box sx={{ flex: 1, pl: 1 }}><AiVoortgang job={ai.job} compact onStop={ai.stop} /></Box>}
          <Button variant="contained" disableElevation onClick={neemAiOver} disabled={aiOpnieuwBezig} sx={{ textTransform: 'none', fontWeight: 700 }}>
            {aiAantalAan} onderdeel{aiAantalAan === 1 ? '' : 'en'} overnemen
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Het callsheet zelf: secties in PDF-volgorde (SECTIE_VOLGORDE), één kaart ── */}
      <Paper variant="outlined" data-testid="callsheet-document" sx={{ px: { xs: 2, md: 3.5 }, py: 2.5, borderRadius: 2, borderColor: '#e5e7eb', mb: 3, '& .MuiTypography-body2:not(.rij-label):not(.cs-nadruk)': { fontSize: WAARDE_PX, lineHeight: REGELHOOGTE } }}>
        <Sectie id="cs-sectie-wanneer" titel={SECTIE_TITEL.wanneer} accent={SECTIE_ACCENT.waar} uitgelicht>
          <Rij label="Tijden">
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
              <KlikVeld veld="begintijd" label="Begintijd" soort="tijd" ctx={editCtx} waarde={extra.begintijd}
                ontbreekt={isOpen('begintijd')} leeg="begin" waardeSx={tijdGrootSx}
                onBewaar={(v) => bewaarExtra({ begintijd: v, tijdenBron: 'handmatig' })} />
              <Typography component="span" variant="body2" className="cs-nadruk" sx={{ color: GRIJS, fontSize: 20 }}>–</Typography>
              <KlikVeld veld="eindtijd" label="Eindtijd" soort="tijd" ctx={editCtx} waarde={extra.eindtijd}
                ontbreekt={isOpen('eindtijd')} leeg="eind" waardeSx={tijdGrootSx}
                onBewaar={(v) => bewaarExtra({ eindtijd: v, tijdenBron: 'handmatig' })} />
              <Tooltip title={planningGesorteerd.length ? 'Begin = eerste planningsregel, eind = laatste regel + duur' : 'Vul eerst de planning in'}>
                <span>
                  <Button size="small" onClick={tijdenUitPlanning} disabled={!planningGesorteerd.length} sx={{ ...tekstKnopSx, color: GRIJS }}>
                    Tijden uit planning
                  </Button>
                </span>
              </Tooltip>
              {extra.tijdenBron === 'planning' && <Typography variant="caption" sx={{ color: GRIJS }}>volgt de planning</Typography>}
            </Box>
          </Rij>
          <Rij label="Settelefoon">
            <KlikVeld veld="settelefoon" label="Settelefoon" ctx={editCtx} ontbreekt={isOpen('settelefoon')}
              waarde={settel.naam || settel.nummer}
              // Settelefoon is altijd Full Frame: naam zonder bedrijf uit Personen, nummer op een eigen regel.
              weergave={(
                <>
                  {settel.naam ? `${stripBedrijf(settel.naam)} (Full Frame)` : 'Full Frame'}
                  {settel.nummer && <Box component="span" sx={{ display: 'block', ...tijdSx(INKT) }}>{formatTelefoon(settel.nummer)}</Box>}
                </>
              )}
              editor={({ sluit }) => <SettelefoonZoekVeld personen={personen} onKies={kiesSettelefoon} onAnnuleer={sluit} />} />
          </Rij>
          <Rij label="Contact klant">
            <KlikVeld veld="contact" label="Contact klant" ctx={editCtx} ontbreekt={isOpen('contact')}
              waarde={contactNaam.soort === 'geen' ? 'geen' : contactNaam.waarde} bewerkWaarde={contactNaam.handmatig}
              weergave={contactNaam.soort === 'project' ? grijsTekst(`uit project: ${contactNaam.waarde}`)
                : contactNaam.soort === 'geen' ? grijsTekst('geen contact op deze callsheet') : undefined}
              editor={({ sluit }) => (
                <KlantContactZoekVeld begin={contactNaam.handmatig} onAnnuleer={sluit}
                  origineel={contactNaam.soort === 'geen' ? GEEN_CONTACT : contactNaam.handmatig}
                  onKies={(c) => { sluit(); bewaarContact({ 'Contactpersoon (handmatig)': c.naam, 'Contactgegevens klant telefoon (Handmatig)': c.telefoon || '' }); }}
                  onHandmatig={(naam) => { sluit(); bewaarContact({ 'Contactpersoon (handmatig)': naam }); }}
                  onGeenContact={contactNaam.soort === 'geen' ? undefined : () => { sluit(); bewaarContact({ 'Contactpersoon (handmatig)': GEEN_CONTACT, 'Contactgegevens klant telefoon (Handmatig)': GEEN_CONTACT }); }} />
              )} />
            {/* Alleen zonder open contactveld: anders kan een concept na herstel alsnog bewaren (Codex r5). */}
            {contactNaam.soort === 'geen' && !['contact', 'Contactgegevens klant telefoon (Handmatig)'].includes(editingField) && (
              // onMouseDown: geen blur van een open contactveld vóór deze klik (Codex r4).
              <Button size="small" onMouseDown={(e) => e.preventDefault()}
                onClick={() => bewaarContact({ 'Contactpersoon (handmatig)': '', 'Contactgegevens klant telefoon (Handmatig)': '' })}
                sx={{ ...tekstKnopSx, color: GRIJS, ml: 1 }}>
                Gebruik contact uit project
              </Button>
            )}
          </Rij>
          <Rij label="Telefoon klant">
            <KlikVeld veld="Contactgegevens klant telefoon (Handmatig)" label="Telefoon klant" ctx={editCtx}
              ontbreekt={isOpen('telefoon')}
              waarde={contactTel.soort === 'geen' ? 'geen' : contactTel.waarde} bewerkWaarde={contactTel.handmatig}
              origineel={contactTel.soort === 'geen' ? GEEN_CONTACT : contactTel.handmatig}
              weergave={contactTel.soort === 'project' ? grijsTekst(`uit project: ${formatTelefoon(contactTel.waarde)}`)
                : contactTel.soort === 'geen' ? grijsTekst('geen nummer op deze callsheet') : formatTelefoon(contactTel.waarde)}
              hulp="Enter bewaart · leegmaken = terug naar het nummer uit het project · Esc annuleert"
              onBewaar={(v) => bewaarContact({ 'Contactgegevens klant telefoon (Handmatig)': String(v || '').trim() })} />
          </Rij>
          {projectleider && <Rij label="Projectleider"><Typography variant="body2">{projectleider}</Typography></Rij>}
        </Sectie>

        {oplevering.length > 0 && (
          <Sectie id="cs-sectie-oplevering" titel={SECTIE_TITEL.oplevering} accent={SECTIE_ACCENT.oplevering}>
            <KlikVeld veld="oplevering" label="Oplevering" soort="lang" ctx={editCtx} waarde={oplevering.join('\n')}
              weergave={<TekstOpmaak tekst={oplevering.map((r) => `- ${r}`).join('\n')} />}
              onBewaar={(v) => updateField('Algemene opmerkingen', voegOpleveringSamen(opmerkingenTekst, String(v || '').split('\n')))} />
          </Sectie>
        )}

        <Sectie id="cs-sectie-opmerkingen" titel={SECTIE_TITEL.opmerkingen} accent={SECTIE_ACCENT.opmerkingen}>
          <Rij label="Algemeen">
            <KlikVeld veld="Algemene opmerkingen" label="Algemene opmerkingen" soort="lang" ctx={editCtx} waarde={opmerkingenTekst}
              leeg="— alleen wat nergens anders staat: uitloop, sfeer, bijzonderheden"
              onBewaar={(v) => updateField('Algemene opmerkingen', voegOpleveringSamen(v, oplevering))} />
          </Rij>
          {[
            { fieldName: 'Props', label: 'Props' },
            { fieldName: 'Styling', label: 'Styling' },
            { fieldName: 'Visagie', label: 'Visagie & haar' },
            { fieldName: 'Lunch', label: 'Lunch / catering' },
            { fieldName: 'Kledingvoorschriften', label: 'Kleding' },
            { fieldName: 'Reminder', label: 'Reminder' },
          ].map(f => (
            <Rij key={f.fieldName} label={f.label}>
              <KlikVeld veld={f.fieldName} label={f.label} soort="lang" ctx={editCtx} waarde={fields[f.fieldName]}
                onBewaar={(v) => updateField(f.fieldName, v)} />
            </Rij>
          ))}
          <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', mt: 0.5 }}>Een leeg veld komt niet op de PDF.</Typography>
        </Sectie>

        <Sectie id="cs-sectie-waar" titel={SECTIE_TITEL.waar} accent={SECTIE_ACCENT.waar}>
          {weather && <Rij label="Weer"><Typography variant="body2">{weather}</Typography></Rij>}
          <Rij label={locaties.length > 1 ? 'Locaties' : 'Locatie'} id="cs-rij-locatie">
            {locaties.length === 0 && (
              <Typography variant="body2" component="span" sx={{ color: ORANJE, fontWeight: 600, mr: 1 }}>ontbreekt</Typography>
            )}
            {locaties.map((loc, locIndex) => {
              const lf = loc.fields || {};
              const adres = lf.Adres || [lf.Straat, lf.Postcode, lf.Plaats].filter(Boolean).join(' ');
              const adresRegel = lf.Adres || [lf.Straat, [lf.Postcode, lf.Plaats].filter(Boolean).join(' ')].filter(Boolean).join(', ');
              const pr = parking[loc.id];
              const v = pr?.verdict;
              // Google Maps 'parkeren'-zoekopdracht bij de locatie → zelf de situatie checken (satelliet/streetview).
              const mapsUrl = adres ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`parkeren ${adres}`)}` : null;
              return (
                <Box key={loc.id} data-testid="locatie" sx={{ mb: locIndex < locaties.length - 1 ? 1.5 : 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                    <Box role="button" tabIndex={0} title="Klik om te bewerken"
                      onClick={() => setLocatieDialog({ open: true, data: loc })}
                      onKeyDown={(e) => { if (e.key === 'Enter') setLocatieDialog({ open: true, data: loc }); }}
                      sx={{ cursor: 'pointer', borderRadius: 0.75, mx: -0.75, px: 0.75, '&:hover': { bgcolor: 'rgba(15,23,42,0.05)' } }}>
                      <Typography component="span" variant="body2" className="cs-nadruk" sx={{ fontWeight: 700, fontSize: 16.5, color: INKT }}>
                        {locaties.length > 1 ? `${locIndex + 1}. ` : ''}{lf['Naam locatie'] || 'Locatie'}
                      </Typography>
                      {adresRegel && <Typography component="span" variant="body2" sx={{ color: '#334155' }}> · {adresRegel}</Typography>}
                    </Box>
                    <Box sx={{ ml: 'auto', display: 'flex', flexShrink: 0 }}>
                      {locaties.length > 1 && (
                        <>
                          <Tooltip title="Eerder aandoen"><span><IconButton size="small" sx={rijKnopSx} disabled={locIndex === 0} onClick={() => verplaatsLocatie(locIndex, -1)} aria-label="Eerder aandoen"><ArrowUpIcon sx={{ fontSize: 16 }} /></IconButton></span></Tooltip>
                          <Tooltip title="Later aandoen"><span><IconButton size="small" sx={rijKnopSx} disabled={locIndex === locaties.length - 1} onClick={() => verplaatsLocatie(locIndex, 1)} aria-label="Later aandoen"><ArrowDownIcon sx={{ fontSize: 16 }} /></IconButton></span></Tooltip>
                        </>
                      )}
                      <Tooltip title="Locatie verwijderen"><IconButton size="small" sx={rijKnopSx} onClick={() => deleteLocatie(loc.id)} aria-label="Locatie verwijderen"><CloseIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                    </Box>
                  </Box>
                  <ReistijdRegel adres={adresRegel} datum={draaidagIso} aankomst={extra.begintijd} kleur={GRIJS} />
                  {(lf.Contactpersoon || lf.Telefoonnummer) && (
                    <Typography variant="body2" sx={{ color: GRIJS }}>Ter plaatse: {[lf.Contactpersoon, formatTelefoon(lf.Telefoonnummer)].filter(Boolean).join(' · ')}</Typography>
                  )}
                  {lf.Opmerkingen && <Typography variant="body2" sx={{ color: GRIJS, whiteSpace: 'pre-wrap' }}>{lf.Opmerkingen}</Typography>}
                  <Typography variant="body2" sx={{ color: lf.Parkeren ? '#334155' : GRIJS, whiteSpace: 'pre-wrap' }}>
                    Parkeren: {lf.Parkeren || 'nog niet ingevuld'}
                  </Typography>
                  <Box sx={{ my: 0.5 }}>
                    <FotoStrook api={fotoApi} doel={{ locatieId: loc.id }} label="Foto's van de locatie" />
                  </Box>
                  {lf.Parkeerfoto && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, my: 0.5 }}>
                      {String(lf.Parkeerfoto).split('\n').map(x => x.trim()).filter(Boolean).map((url) => (
                        <Box key={url} component="a" href={url} target="_blank" rel="noopener noreferrer">
                          <Box component="img" src={url} alt="parkeerfoto"
                            sx={{ width: 72, height: 54, objectFit: 'cover', borderRadius: 0.5, border: '1px solid #e5e7eb' }} />
                        </Box>
                      ))}
                    </Box>
                  )}
                  {pr && !pr.error && (
                    <Box sx={{ mt: 0.25 }}>
                      <Typography variant="body2" sx={{ color: '#334155' }}>
                        <b>Parkeercheck: {v?.label || 'Parkeren'}</b>
                        {pr.nearestGarage ? ` · garage ${pr.nearestGarage.name} ~${pr.nearestGarage.distanceM} m` : ''}
                      </Typography>
                      {v?.message && <Typography variant="body2" sx={{ color: GRIJS }}>{v.message}</Typography>}
                    </Box>
                  )}
                  {pr?.error && <Typography variant="body2" color="error">Parkeercheck: {pr.error}</Typography>}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', ml: -1 }}>
                    {pr && !pr.error && (
                      <Button size="small" onClick={() => saveParkingNote(loc, v?.message || '')} sx={tekstKnopSx}>Opslaan bij locatie</Button>
                    )}
                    <Button size="small" onClick={() => checkParking(loc)} disabled={parkingLoading[loc.id]} sx={{ ...tekstKnopSx, color: GRIJS }}>
                      {parkingLoading[loc.id] ? 'Parkeren checken…' : pr ? 'Opnieuw checken' : 'Parkeren checken'}
                    </Button>
                    {mapsUrl && (
                      <Button size="small" component="a" href={mapsUrl} target="_blank" rel="noopener noreferrer" sx={{ ...tekstKnopSx, color: GRIJS }}>
                        Parkeren op Google Maps
                      </Button>
                    )}
                  </Box>
                </Box>
              );
            })}
            <Button size="small" onClick={() => setLocatieDialog({ open: true, data: null })} sx={{ ...tekstKnopSx, ml: -1 }}>
              + Locatie toevoegen
            </Button>
          </Rij>
        </Sectie>

        <Sectie id="cs-sectie-wie" titel={SECTIE_TITEL.wie} accent={SECTIE_ACCENT.wie}>
          <Rij label="Crew" id="cs-rij-crew">
            {!(crew || []).length && <Typography variant="body2" component="span" sx={{ color: ORANJE, fontWeight: 600, mr: 1 }}>ontbreekt</Typography>}
            {(crew || []).length > 0 && (() => {
              const sortedCrew = sortByVolgorde(crew);
              return (
                <Table size="small" sx={{ '& td': { borderBottom: `1px solid ${LIJN}`, py: 0.6, px: 0.75, fontSize: WAARDE_PX, lineHeight: REGELHOOGTE } }}>
                  <TableBody>
                    {sortedCrew.map((c) => {
                      const tel = formatTelefoon(c.fields?.['Telefoon (handmatig)'] || c.fields?.['Telefoonnummer (from Personen)']?.[0] || c.fields?.Telefoonnummer || '');
                      return (
                        <TableRow key={c.id} data-testid="crew-rij"
                          onDragOver={(e) => { if (draggedRow?.key === 'crew') e.preventDefault(); }}
                          onDrop={() => handleRowDrop('crew', 'callsheet-crew', sortedCrew, c.id)}
                          sx={{ opacity: draggedRow?.id === c.id ? 0.4 : 1, '&:hover .rij-acties': { opacity: 1 } }}>
                          <TableCell sx={{ width: 20, px: '0 !important', cursor: 'grab', color: '#cbd5e1' }} draggable
                            onDragStart={() => setDraggedRow({ key: 'crew', id: c.id })} onDragEnd={() => setDraggedRow(null)}>
                            <DragIcon sx={{ fontSize: 16 }} />
                          </TableCell>
                          <TableCell onClick={() => setCrewDialog({ open: true, data: c })} sx={{ cursor: 'pointer', fontWeight: 600, color: INKT }} title="Klik om te bewerken">
                            {crewNaam(c)}
                          </TableCell>
                          <TableCell onClick={() => setCrewDialog({ open: true, data: c })} sx={{ cursor: 'pointer' }}><RolChip functie={c.fields?.['Functie (handmatig)']} /></TableCell>
                          <TableCell onClick={() => setCrewDialog({ open: true, data: c })} sx={{ cursor: 'pointer', color: GRIJS, whiteSpace: 'nowrap' }}>{tel}</TableCell>
                          <TableCell onClick={() => setCrewDialog({ open: true, data: c })} sx={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>{callTijd(c.fields?.Calltijd)}</TableCell>
                          <TableCell sx={{ width: 32 }}>
                            <IconButton className="rij-acties" size="small" sx={{ ...rijKnopSx, opacity: { xs: 1, md: 0.4 } }} onClick={() => deleteCrew(c.id)} aria-label={`${crewNaam(c)} verwijderen`}>
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              );
            })()}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', ml: -1 }}>
              <Button size="small" onClick={() => setCrewDialog({ open: true, data: null })} sx={tekstKnopSx}>+ Crewlid</Button>
              {(crew || []).length > 0 && (planning || []).length > 0 && (
                <Tooltip title="Zet de calltijd van alle crew gelijk aan de eerste call-tijd van de dag">
                  <Button size="small" onClick={setAllCrewToFirstCalltime} sx={{ ...tekstKnopSx, color: GRIJS }}>Calltijd = eerste call</Button>
                </Tooltip>
              )}
            </Box>
          </Rij>
          <Rij label="Cast & aanwezigen">
            {(cast || []).length > 0 && (() => {
              const sortedCast = sortByVolgorde(cast);
              return (
                <Table size="small" sx={{ '& td': { borderBottom: `1px solid ${LIJN}`, py: 0.6, px: 0.75, fontSize: WAARDE_PX, lineHeight: REGELHOOGTE } }}>
                  <TableBody>
                    {sortedCast.map((c) => (
                      <TableRow key={c.id} data-testid="cast-rij"
                        onDragOver={(e) => { if (draggedRow?.key === 'cast') e.preventDefault(); }}
                        onDrop={() => handleRowDrop('cast', 'callsheet-cast', sortedCast, c.id)}
                        sx={{ opacity: draggedRow?.id === c.id ? 0.4 : 1, '&:hover .rij-acties': { opacity: 1 } }}>
                        <TableCell sx={{ width: 20, px: '0 !important', cursor: 'grab', color: '#cbd5e1' }} draggable
                          onDragStart={() => setDraggedRow({ key: 'cast', id: c.id })} onDragEnd={() => setDraggedRow(null)}>
                          <DragIcon sx={{ fontSize: 16 }} />
                        </TableCell>
                        <TableCell onClick={() => setCastDialog({ open: true, data: c })} sx={{ cursor: 'pointer' }} title="Klik om te bewerken">{castNaam(c)}</TableCell>
                        <TableCell onClick={() => setCastDialog({ open: true, data: c })} sx={{ cursor: 'pointer', color: '#334155' }}>{c.fields?.Rol || ''}</TableCell>
                        <TableCell onClick={() => setCastDialog({ open: true, data: c })} sx={{ cursor: 'pointer', color: GRIJS, whiteSpace: 'nowrap' }}>{formatTelefoon(c.fields?.['Telefoon (handmatig)'])}</TableCell>
                        <TableCell onClick={() => setCastDialog({ open: true, data: c })} sx={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>{callTijd(c.fields?.Calltijd)}</TableCell>
                        <TableCell sx={{ width: 32 }}>
                          <IconButton className="rij-acties" size="small" sx={{ ...rijKnopSx, opacity: { xs: 1, md: 0.4 } }} onClick={() => deleteCast(c.id)} aria-label={`${castNaam(c)} verwijderen`}>
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              );
            })()}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', ml: -1 }}>
              <Button size="small" onClick={() => setCastDialog({ open: true, data: null })} sx={tekstKnopSx}>+ Cast of aanwezige</Button>
              {(cast || []).length > 0 && (planning || []).length > 0 && (
                <Tooltip title="Zet de calltijd van alle cast gelijk aan de eerste call-tijd van de dag">
                  <Button size="small" onClick={setAllCastToFirstCalltime} sx={{ ...tekstKnopSx, color: GRIJS }}>Calltijd = eerste call</Button>
                </Tooltip>
              )}
            </Box>
          </Rij>
        </Sectie>

        <Sectie id="cs-sectie-planning" titel={SECTIE_TITEL.planning} accent={SECTIE_ACCENT.planning} acties={(
          <>
            <Tooltip title="Kopieer de planning van een andere draaidag">
              <span><Button size="small" onClick={handleOpenCopyDialog} disabled={loadingTemplate} sx={{ ...tekstKnopSx, color: GRIJS }}>
                {loadingTemplate ? <CircularProgress size={14} /> : 'Kopieer van draaidag'}
              </Button></span>
            </Tooltip>
            <Tooltip title="Laad standaard planningsregels uit de template">
              <span><Button size="small" onClick={handleLoadFromTemplate} disabled={loadingTemplate} sx={{ ...tekstKnopSx, color: GRIJS }}>Template</Button></span>
            </Tooltip>
            {(planning || []).length > 1 && (
              <Tooltip title="Zet alle regels achter elkaar op basis van duur (parallelle onderdelen schuiven dan mee). Regels met een slotje blijven staan. Daarna kun je het ongedaan maken.">
                <span><Button size="small" disabled={planningBezig} onClick={() => recalculatePlanningTimes()} sx={{ ...tekstKnopSx, color: GRIJS }}>Herbereken tijden</Button></span>
              </Tooltip>
            )}
            <Button size="small" onClick={() => nieuweRegel((planning || []).length)} sx={tekstKnopSx}>+ Regel</Button>
          </>
        )}>
          {planning && planning.length > 0 ? (
            <Box sx={{ overflowX: 'auto', mx: -0.75, pb: 1.5 }}>
              <Table size="small" sx={{ '& td, & th': { borderBottom: `1px solid ${LIJN}`, px: 0.75 }, '& th': { color: GRIJS, fontWeight: 700, fontSize: 11.5, letterSpacing: '0.06em', textTransform: 'uppercase' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell width={28}></TableCell>
                    <TableCell>Tijd</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Duur</TableCell>
                    <TableCell>Omschrijving</TableCell>
                    <TableCell>Locatie</TableCell>
                    <TableCell>Cast</TableCell>
                    <TableCell>Opmerkingen</TableCell>
                    <TableCell>Foto's</TableCell>
                    <TableCell width={96}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sorteerPlanning(planning).map((p, index, arr) => {
                    const vast = lockedRows.has(p.id);
                    const lijn = dropDoel?.id === p.id && sleepRegel && sleepRegel !== p.id ? dropDoel.positie : null;
                    // Doelpositie in de lijst zónder de versleepte regel.
                    const doelIndex = (positie) => {
                      const zonder = arr.filter((x) => x.id !== sleepRegel);
                      const i = zonder.findIndex((x) => x.id === p.id);
                      return positie === 'boven' ? i : i + 1;
                    };
                    // Bovenste helft van de rij = invoegen erboven, onderste helft = eronder.
                    const positieIn = (e) => {
                      const r = e.currentTarget.getBoundingClientRect();
                      return e.clientY < r.top + r.height / 2 ? 'boven' : 'onder';
                    };
                    const invoegKnop = (idx, label, boven) => (
                      <Tooltip title="Regel hier invoegen" placement="left">
                        <IconButton className="invoeg" size="small" aria-label={label} onClick={() => nieuweRegel(idx)}
                          sx={{ position: 'absolute', left: 4, [boven ? 'top' : 'bottom']: -11, zIndex: 1, p: 0.125,
                            bgcolor: '#fff', border: `1px solid ${LIJN}`, color: '#64748b', opacity: 0,
                            '&:focus-visible': { opacity: 1 }, '&:hover': { color: '#1d4ed8', bgcolor: '#fff' } }}>
                          <AddIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    );
                    return (
                    <TableRow key={p.id} data-testid="planning-rij" tabIndex={0}
                      aria-label={`${formatDuration(p.fields?.Tijd)} ${p.fields?.Omschrijving || ''} — sleep of Alt+pijl om te verplaatsen`}
                      onKeyDown={(e) => {
                        // Toetsenbord: Alt+↑/↓ op de gefocuste rij ruilt met de buur (zelfde regel als ▲▼ vroeger).
                        if (e.target !== e.currentTarget || !e.altKey) return;
                        if (e.key === 'ArrowUp') { e.preventDefault(); movePlanningItem(p.id, 'up'); }
                        if (e.key === 'ArrowDown') { e.preventDefault(); movePlanningItem(p.id, 'down'); }
                      }}
                      onDragOver={(e) => {
                        if (!sleepRegel) return;
                        e.preventDefault();
                        const positie = positieIn(e);
                        if (dropDoel?.id !== p.id || dropDoel?.positie !== positie) setDropDoel({ id: p.id, positie });
                      }}
                      onDragLeave={() => { if (dropDoel?.id === p.id) setDropDoel(null); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const positie = positieIn(e);   // uit het drop-event zelf: de state kan achterlopen
                        const gesleept = sleepRegel;
                        setSleepRegel(null); setDropDoel(null);
                        if (gesleept && gesleept !== p.id) verplaatsNaar(gesleept, doelIndex(positie));
                      }}
                      sx={{
                        opacity: sleepRegel === p.id ? 0.4 : 1, outline: 'none',
                        '&:hover .rij-acties, &:focus-within .rij-acties': { opacity: 1 },
                        '&:hover .invoeg, &:focus-within .invoeg': { opacity: 1 },
                        '&:focus-visible': { bgcolor: 'rgba(29,78,216,0.04)' },
                        // Invoeglijn tijdens het slepen.
                        ...(lijn ? { '& > td': { [lijn === 'boven' ? 'borderTop' : 'borderBottom']: '2px solid #1d4ed8' } } : {}),
                      }}>
                      <TableCell sx={{ position: 'relative', px: '2px !important', width: 28 }}>
                        {index === 0 && invoegKnop(0, 'Regel invoegen boven de eerste regel', true)}
                        {invoegKnop(index + 1, `Regel invoegen na ${p.fields?.Omschrijving || formatDuration(p.fields?.Tijd)}`, false)}
                        <Box draggable={!vast && !planningBezig}
                          onDragStart={(e) => { e.dataTransfer?.setData?.('text/plain', p.id); setSleepRegel(p.id); }}
                          onDragEnd={() => { setSleepRegel(null); setDropDoel(null); }}
                          title={vast ? 'Vaste begintijd (slotje): niet te verslepen' : 'Sleep om te verplaatsen (of Alt+↑/↓)'}
                          aria-label={vast ? 'Vaste regel, niet te verslepen' : 'Versleep regel'}
                          data-testid="sleep-greep"
                          sx={{ display: 'flex', cursor: vast ? 'not-allowed' : 'grab', color: vast ? '#e2e8f0' : '#cbd5e1', '&:hover': { color: vast ? '#e2e8f0' : '#64748b' } }}>
                          <DragIcon sx={{ fontSize: 16 }} />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...tijdSx(SECTIE_ACCENT.planning.kleur), fontSize: WAARDE_PX + 1, whiteSpace: 'nowrap', bgcolor: lockedRows.has(p.id) ? '#fff7ed' : undefined }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <Tooltip title={lockedRows.has(p.id) ? 'Begintijd staat vast — blijft staan bij herberekenen; wat erna komt sluit hierop aan. Klik om vrij te geven.' : 'Begintijd vastzetten (bv. lunch of een afspraak met de klant): blijft staan bij herberekenen, de rest sluit erop aan'}>
                            <IconButton size="small" onClick={() => toggleLock(p.id)} sx={{ p: 0.25, color: lockedRows.has(p.id) ? '#ea580c' : '#cbd5e1' }}>
                              {lockedRows.has(p.id) ? <LockIcon sx={{ fontSize: 15 }} /> : <LockOpenIcon sx={{ fontSize: 15 }} />}
                            </IconButton>
                          </Tooltip>
                          {renderPlanningCell(p, 'Tijd', formatDuration(p.fields?.Tijd) || '-')}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: GRIJS }}>
                        {renderPlanningCell(p, 'Duur (minuten)', p.fields?.['Duur (minuten)'] ? `${p.fields['Duur (minuten)']} min` : '-')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: INKT }}>{renderPlanningCell(p, 'Omschrijving', p.fields?.Omschrijving || '-')}</TableCell>
                      <TableCell>
                        <PlanningCombo
                          value={p.fields?.Locatie || ''}
                          options={locaties.map(l => l.fields?.['Naam locatie'] || l.fields?.Naam || '').filter(Boolean)}
                          onCommit={(v) => setPlanningField(p.id, 'Locatie', v)}
                          placeholder="Locatie"
                        />
                      </TableCell>
                      <TableCell>
                        <PlanningCastMulti
                          value={p.fields?.Cast || ''}
                          options={(cast || []).map(castNaam).filter((n) => n && n !== '-')}
                          onCommit={(v) => setPlanningField(p.id, 'Cast', v)}
                          placeholder="Cast"
                        />
                      </TableCell>
                      <TableCell>{renderPlanningCell(p, 'Opmerkingen', p.fields?.Opmerkingen || '-')}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <PlanningFotoCel api={fotoApi} regel={p} locaties={locaties} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Box className="rij-acties" sx={{ opacity: { xs: 1, md: 0.4 } }}>
                          <Tooltip title="Alle velden van deze regel"><IconButton size="small" sx={rijKnopSx} onClick={() => setPlanningDialog({ open: true, data: p })} aria-label="Regel bewerken"><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                          <Tooltip title="Regel dupliceren"><IconButton size="small" sx={rijKnopSx} onClick={() => duplicatePlanning(p)} aria-label="Regel dupliceren"><ContentCopyIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                          <Tooltip title="Regel verwijderen"><IconButton size="small" sx={rijKnopSx} onClick={() => deletePlanning(p.id)} aria-label="Regel verwijderen"><CloseIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: GRIJS }}>Nog geen planning.</Typography>
          )}
        </Sectie>
      </Paper>

      {/* ── Briefing crew: apart document, alleen voor de crew (PG callsheet_extra.briefing) ── */}
      <Paper variant="outlined" data-testid="briefing-crew" sx={{ px: { xs: 2, md: 3.5 }, py: 2.5, borderRadius: 2, borderColor: '#e5e7eb', bgcolor: '#fcfcfd', '& .MuiTypography-body2:not(.rij-label):not(.cs-nadruk)': { fontSize: WAARDE_PX, lineHeight: REGELHOOGTE } }}>
        <Sectie id="cs-sectie-briefing" titel={SECTIE_TITEL.briefing} accent={SECTIE_ACCENT.briefing} acties={(
          <>
            {extra.briefing && !briefingBewerk && (
              <Button size="small" disabled={briefingActie === 'pdf'} onClick={downloadBriefingPdf} sx={tekstKnopSx}>
                {briefingActie === 'pdf' ? 'PDF maken…' : 'PDF'}
              </Button>
            )}
            {extra.briefing && !briefingBewerk && (
              <Button size="small" disabled={briefingActie === 'mail'} onClick={conceptMailCrew} sx={tekstKnopSx}>
                {briefingActie === 'mail' ? 'Concept maken…' : 'Concept-mail crew'}
              </Button>
            )}
            {extra.briefing && !briefingBewerk && <Button size="small" onClick={naarShotlist} sx={tekstKnopSx}>Shotlist maken</Button>}
            <Button size="small" onClick={() => navigate(`/callsheets/${id}/briefing`)} sx={tekstKnopSx}>Openen als document</Button>
          </>
        )}>
          <Typography variant="body2" sx={{ color: GRIJS, mb: 1.5 }}>
            Alleen voor de crew — de klant ziet dit niet. Doel van de film, wat er zeker gefilmd moet worden, stijl en valkuilen.
          </Typography>
          {briefingBewerk || !extra.briefing ? (
            <>
              <TextField fullWidth multiline minRows={6} maxRows={30} value={briefingTekst} onChange={(e) => setBriefingTekst(e.target.value)}
                autoFocus={briefingBewerk}
                onKeyDown={(e) => { if (e.key === 'Escape' && briefingBewerk) { setBriefingTekst(extra.briefing || ''); setBriefingBewerk(false); } }}
                placeholder="Nog geen briefing. Kies ‘AI voorbereiden’ bovenaan, of schrijf hem zelf (markdown mag)."
                inputProps={{ 'aria-label': 'Briefing crew', dir: 'ltr', style: { textAlign: 'left', fontSize: 13.5, lineHeight: 1.5 } }} />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                {briefingBewerk && <Button size="small" onClick={() => { setBriefingTekst(extra.briefing || ''); setBriefingBewerk(false); }} sx={tekstKnopSx}>Annuleren</Button>}
                <Button size="small" variant="outlined" disabled={briefingBezig || briefingTekst === (extra.briefing || '')}
                  onClick={async () => { setBriefingBezig(true); const ok = await bewaarExtra({ briefing: briefingTekst }); setBriefingBezig(false); if (ok) setBriefingBewerk(false); }}
                  sx={{ textTransform: 'none', fontWeight: 600 }}>
                  {briefingBezig ? 'Opslaan…' : 'Briefing opslaan'}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Box role="button" tabIndex={0} title="Klik om te bewerken"
                onClick={(e) => { if (e.target.closest?.('a')) return; setBriefingBewerk(true); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) setBriefingBewerk(true); }}
                sx={{ cursor: 'text', borderRadius: 1, mx: -1, px: 1, '&:hover': { bgcolor: 'rgba(15,23,42,0.03)' }, ...tekstOpmaakSx, '& h1, & h2, & h3, & h4': { fontSize: WAARDE_PX, fontWeight: 800, mt: 2, mb: 0.5 } }}>
                {/* Zelfde structuur als de andere lange teksten: losse regels als bullets (alleen weergave). */}
                <ReactMarkdown components={mdLinkComponents}>{losseRegelsAlsLijst(bronnenTonen ? bronRefsNaarLinks(briefingTekst, extra.briefingBronnen || []) : stripBronRefs(briefingTekst))}</ReactMarkdown>
              </Box>
              <FormControlLabel sx={{ mt: 1, ml: 0 }} control={<Switch size="small" checked={bronnenTonen} onChange={(e) => setBronnenTonen(e.target.checked)} />}
                label={<Typography variant="caption" sx={{ color: GRIJS }}>Bronnen tonen (alleen voor jou; crew en PDF zien ze niet)</Typography>} />
            </>
          )}
        </Sectie>
      </Paper>

      <CallsheetBijlagen callsheetId={id} onOverzicht={setBijlagenOverzicht} paperSx={{ mt: 3, mb: 0, borderRadius: 2, boxShadow: 0 }} />

      {/* Dialogs */}
      <CrewDialog 
        open={crewDialog.open} 
        onClose={() => setCrewDialog({ open: false, data: null })}
        onSave={refreshData}
        initialData={crewDialog.data}
        callsheetId={id}
        personen={personen}
      />
      <CastDialog 
        open={castDialog.open} 
        onClose={() => setCastDialog({ open: false, data: null })}
        onSave={refreshData}
        initialData={castDialog.data}
        callsheetId={id}
        castingOptions={castingOptions}
      />
      <PlanningDialog 
        open={planningDialog.open} 
        onClose={() => setPlanningDialog({ open: false, data: null })}
        onSave={() => { planningGewijzigd(); refreshData(); }}
        initialData={planningDialog.data}
        standaard={planningDialog.standaard}
        callsheetId={id}
      />
      <LocatieDialog 
        open={locatieDialog.open} 
        onClose={() => setLocatieDialog({ open: false, data: null })}
        onSave={refreshData}
        initialData={locatieDialog.data}
        callsheetId={id}
        existingLocaties={existingLocaties}
      />

      {/* PDF-preview (in-app, i.p.v. nieuw tabblad) */}
      <Dialog open={pdfPreview.open} onClose={closePdfPreview} maxWidth="lg" fullWidth
        PaperProps={{ sx: { height: '92vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
          <PdfIcon sx={{ color: '#dc2626' }} />
          <Typography variant="h6" component="span">PDF-preview</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {pdfPreview.filename}
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={savePdfAs}>
              Opslaan als…
            </Button>
            <IconButton onClick={closePdfPreview}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {pdfPreview.url && (
            // Zonder werkbalk van de ingebouwde PDF-viewer: die slaat een blob op onder een uuid-naam.
            // Opslaan gaat via "Opslaan als…" hierboven, met de juiste naam (Joris 29 sep).
            <Box component="iframe" title="PDF-preview" src={`${pdfPreview.url}#toolbar=0&navpanes=0`}
              sx={{ width: '100%', height: '100%', border: 0, display: 'block' }} />
          )}
        </DialogContent>
      </Dialog>

      {/* Copy Planning Dialog */}
      <Dialog 
        open={copyPlanningDialog} 
        onClose={() => setCopyPlanningDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          📋 Kopieer planning van andere draaidag
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1, p: 1.5, backgroundColor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={clearExistingPlanning}
                  onChange={(e) => setClearExistingPlanning(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    Verwijder bestaande planning eerst
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {clearExistingPlanning
                      ? 'De huidige planning wordt vervangen door de gekopieerde items'
                      : 'De gekopieerde items worden toegevoegd aan de bestaande planning'}
                  </Typography>
                </Box>
              }
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={<Checkbox size="small" checked={copyIncludeCrew} onChange={(e) => setCopyIncludeCrew(e.target.checked)} />}
                label={<Typography variant="body2">Ook crew meekopiëren</Typography>}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={copyIncludeCast} onChange={(e) => setCopyIncludeCast(e.target.checked)} />}
                label={<Typography variant="body2">Ook cast meekopiëren</Typography>}
              />
            </Box>
          </Box>

          <TextField
            fullWidth
            placeholder="Zoek op titel of project..."
            value={copyPlanningSearch}
            onChange={(e) => setCopyPlanningSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchCopyPlanningOptions()}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <Button size="small" onClick={fetchCopyPlanningOptions}>
                  Zoeken
                </Button>
              )
            }}
          />
          
          {loadingCopyOptions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {/* Same Project Section */}
              {copyPlanningOptions.filter(cs => cs.isSameProject).length > 0 && (
                <>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                    🎬 Zelfde project
                  </Typography>
                  {copyPlanningOptions.filter(cs => cs.isSameProject).map(cs => (
                    <Paper 
                      key={cs.id} 
                      sx={{ 
                        p: 2, mb: 1, cursor: 'pointer', 
                        border: '2px solid #e5e7eb',
                        '&:hover': { borderColor: '#3b82f6', backgroundColor: '#f0f9ff' }
                      }}
                      onClick={() => handleCopyPlanning(cs.id)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography fontWeight={600}>{cs.titel}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {cs.projectName} • {cs.datum ? new Date(cs.datum).toLocaleDateString('nl-NL') : 'Geen datum'}
                          </Typography>
                        </Box>
                        <Chip label={`${cs.planningCount} items`} size="small" color="primary" />
                      </Box>
                    </Paper>
                  ))}
                  <Divider sx={{ my: 2 }} />
                </>
              )}
              
              {/* Other Projects Section */}
              {copyPlanningOptions.filter(cs => !cs.isSameProject).length > 0 && (
                <>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, fontWeight: 600 }}>
                    📁 Andere projecten
                  </Typography>
                  {copyPlanningOptions.filter(cs => !cs.isSameProject).slice(0, 20).map(cs => (
                    <Paper 
                      key={cs.id} 
                      sx={{ 
                        p: 2, mb: 1, cursor: 'pointer',
                        border: '1px solid #e5e7eb',
                        '&:hover': { borderColor: '#9ca3af', backgroundColor: '#f9fafb' }
                      }}
                      onClick={() => handleCopyPlanning(cs.id)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography fontWeight={500}>{cs.titel}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {cs.projectName} • {cs.datum ? new Date(cs.datum).toLocaleDateString('nl-NL') : 'Geen datum'}
                          </Typography>
                        </Box>
                        <Chip label={`${cs.planningCount} items`} size="small" variant="outlined" />
                      </Box>
                    </Paper>
                  ))}
                </>
              )}
              
              {copyPlanningOptions.length === 0 && (
                <Typography color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
                  Geen callsheets met planning gevonden
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyPlanningDialog(false)}>Annuleren</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CallSheetManager;
